import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApiIcon from '@mui/icons-material/Api';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  listTaskImports,
  listFlights,
  fetchFlight,
  createFlight,
  updateFlight,
  generateFlightTasks,
  autoSelectFlight,
  archiveFlight,
  listCreativeTasks,
  fetchCreativeTask,
  createCreativeTask,
  updateCreativeTask,
  assignCreativeTask,
  startCreativeTask,
  submitCreativeTask,
  requestRevisionCreativeTask,
  approveCreativeTask,
  addCreativeTaskComment,
  uploadCreativeTaskFile,
  listAvatars,
  createAvatar,
  updateAvatar,
  listPainPoints,
  createPainPoint,
  updatePainPoint,
  listAngleChoices,
  createAngleChoice,
  updateAngleChoice,
  listHookTypeChoices,
  createHookTypeChoice,
  updateHookTypeChoice,
  listAdTypeChoices,
  createAdTypeChoice,
  updateAdTypeChoice,
  listStyleChoices,
  createStyleChoice,
  updateStyleChoice,
  listDesignerProfiles,
  createDesignerProfile,
  updateDesignerProfile,
  listDesignerGroups,
  fetchDesignerBankSummary,
  createDesignerGroup,
  updateDesignerGroup,
  fetchClientVerticals,
  bulkUpdateTasks,
  deleteCreativeTask,
  generateScript,
  generateScriptVariants,
  rankScriptVariants,
  listTaskScripts,
  thumbTaskScript,
  selectTaskScript,
  listScriptPrompts,
  updateScriptPrompt,
  listSelectionPrompts,
  updateSelectionPrompt,
  exportFlightTasks,
  getDesignerAPIToken,
  regenerateDesignerAPIToken,
  rateCreativeTask,
  launchTaskToFacebook,
  fetchLaunchPrefill,
  fetchFacebookAdStructure,
  fetchFacebookAdStructureDB,
  generateAdCopy,
  listAdCopyPrompts,
  updateAdCopyPrompt,
  listAdConcepts,
  fetchAdConcept,
  createManualBrief,
} from '@src/api/creativeStudio';
import { fetchVerticals } from '@src/api/actions/verticals';
import { fetchClientOffers } from '@src/api/actions/dashboard/clientOffers';
import BriefMarkdownEditor from '@src/components/creative/BriefMarkdownEditor';
import TaskImportReviewModal from '@src/components/creative/TaskImportReviewModal';
import { fetchClientCreativeKnowledge } from '@src/api';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { listAssignmentUsers } from '@src/api/tasks';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { useAuth } from '@src/context';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Ages' },
  { value: '55+', label: '55+' },
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
];

const STATUS_CHIP_SX = {
  draft: { bgcolor: 'grey.300', color: 'grey.800' },
  assigned: { bgcolor: '#c8e6c9', color: '#2e7d32' },       // light green
  in_progress: { bgcolor: '#fff9c4', color: '#f57f17' },     // yellow
  submitted: { bgcolor: '#bbdefb', color: '#1565c0' },       // blue
  needs_revision: { bgcolor: '#ffcdd2', color: '#c62828' },  // red
  approved: { bgcolor: '#4caf50', color: '#fff' },            // green
  complete: { bgcolor: '#4caf50', color: '#fff' },            // green
  cancelled: { bgcolor: 'grey.300', color: 'grey.800' },
};

const FLIGHT_STATUS_COLORS = {
  draft: 'default',
  in_progress: 'warning',
  complete: 'success',
  cancelled: 'default',
};

// Days-until-deadline thresholds for the flight summary row.
const FLIGHT_OVERDUE_DAYS = 0;
const FLIGHT_DUE_SOON_DAYS = 3;

function flightDueState(flight) {
  if (!flight.end_date) return { kind: 'none' };
  if (['complete', 'cancelled'].includes(flight.status)) return { kind: 'none' };
  // Parse YYYY-MM-DD as a local date — Date(...'YYYY-MM-DD') parses as UTC midnight,
  // which can flip a day. Build it manually.
  const parts = String(flight.end_date).split('-');
  if (parts.length !== 3) return { kind: 'none' };
  const end = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((end - today) / 86400000);
  if (diffDays < FLIGHT_OVERDUE_DAYS) return { kind: 'overdue', days: -diffDays };
  if (diffDays <= FLIGHT_DUE_SOON_DAYS) return { kind: 'due_soon', days: diffDays };
  return { kind: 'ok', days: diffDays };
}

// Map a flight to one of four scannable groups for the top-level list.
function flightGroupKey(flight) {
  if (flight.archived) return 'archived';
  if (flight.status === 'complete' || flight.status === 'cancelled') return 'complete';
  if (flight.status === 'draft') return 'draft';
  return 'active';
}

const FLIGHT_GROUPS = [
  { key: 'active', label: 'Active', emptyLabel: 'No active flights.' },
  { key: 'draft', label: 'Draft', emptyLabel: 'No draft flights.' },
  { key: 'complete', label: 'Complete', emptyLabel: 'No complete flights.' },
  { key: 'archived', label: 'Archived', emptyLabel: 'No archived flights.' },
];

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (data && typeof data === 'object') {
    const firstValue = Object.values(data).find((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)));
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (typeof firstValue === 'string' && firstValue.trim()) return firstValue;
  }
  return fallback;
}

const autoSlug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 20);

function formatRelativeTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 45) return 'just now';
  if (diffSec < 90) return '1m ago';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMo = Math.round(diffDay / 30);
  if (diffMo < 12) return `${diffMo}mo ago`;
  return `${Math.round(diffMo / 12)}y ago`;
}

export default function CreativeTasksPage() {
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;

  // Designer bank balances (staff-only header chips)
  const [designerBanks, setDesignerBanks] = useState([]);
  const loadDesignerBanks = useCallback(() => {
    fetchDesignerBankSummary()
      .then((resp) => {
        const rows = Array.isArray(resp?.data) ? resp.data : resp?.data?.results || [];
        setDesignerBanks(rows);
      })
      .catch(() => {}); // chip is informational — never block the page
  }, []);
  useEffect(() => {
    loadDesignerBanks();
  }, [loadDesignerBanks]);

  // Client verticals cache (from creative knowledge)
  const clientVerticalsCacheRef = useRef({});
  const [clientVerticalsVersion, setClientVerticalsVersion] = useState(0);

  const loadClientVerticals = useCallback(async (clientId) => {
    if (!clientId || clientVerticalsCacheRef.current[clientId]) return;
    try {
      const res = await fetchClientVerticals(clientId);
      const data = Array.isArray(res.data) ? res.data : [];
      // Extract unique verticals from creative knowledge records
      const verticalIds = new Set();
      data.forEach((ck) => {
        (ck.verticals || []).forEach((vId) => verticalIds.add(vId));
      });
      clientVerticalsCacheRef.current[clientId] = [...verticalIds];
      setClientVerticalsVersion((v) => v + 1);
    } catch (_err) {
      // Fallback: show all verticals
      clientVerticalsCacheRef.current[clientId] = null;
    }
  }, []);

  const getVerticalsForClient = (clientId) => {
    const cached = clientVerticalsCacheRef.current[clientId];
    if (!cached) return verticals; // fallback: show all
    return verticals.filter((v) => cached.includes(v.id));
  };

  // External task imports (staged push → preview/remap → commit)
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [pendingImportCount, setPendingImportCount] = useState(0);

  // Flights
  const [flights, setFlights] = useState([]);
  const [flightsLoading, setFlightsLoading] = useState(false);
  const [expandedFlightId, setExpandedFlightId] = useState(null);
  const [collapsedFlightGroups, setCollapsedFlightGroups] = useState({ active: false, draft: false, complete: true, archived: true });
  const [flightTasks, setFlightTasks] = useState({});
  const [flightTasksLoading, setFlightTasksLoading] = useState({});
  const [error, setError] = useState('');

  // Filters
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDesigner, setFilterDesigner] = useState('');
  const [filterSource, setFilterSource] = useState(''); // '' = legacy (staff default); concept_cell | all
  const [showArchivedFlights, setShowArchivedFlights] = useState(false);

  // Lookups
  const [verticals, setVerticals] = useState([]);
  const avatarsCacheRef = useRef({}); // { verticalId: [...] } — ref to avoid re-renders
  const painPointsCacheRef = useRef({});
  const [verticalOptionsVersion, setVerticalOptionsVersion] = useState(0); // bump to trigger re-render when cache fills
  const [angles, setAngles] = useState([]);
  const [hookTypes, setHookTypes] = useState([]);
  const [adTypes, setAdTypes] = useState([]);
  const [styles, setStyles] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [designerGroups, setDesignerGroups] = useState([]);

  // Custom script (manual brief) dialog — designers don't have Creative Engine
  // access, so the only entry point is here on the Flights page.
  const [customScriptOpen, setCustomScriptOpen] = useState(false);

  // Create flight dialog
  const [createFlightOpen, setCreateFlightOpen] = useState(false);
  const [creatingFlight, setCreatingFlight] = useState(false);
  const [createFlightError, setCreateFlightError] = useState('');
  const [flightForm, setFlightForm] = useState({
    name: '', designer: '', designer_group: '', start_date: '', end_date: '', notes: '',
  });
  const [flightSegments, setFlightSegments] = useState([{ client: '', vertical: '', count: 5 }]);

  // Generate tasks for existing flight
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generatingTasks, setGeneratingTasks] = useState(false);
  const [generateFlightId, setGenerateFlightId] = useState(null);
  const [generateSegments, setGenerateSegments] = useState([{ client: '', vertical: '', count: 5 }]);

  // Auto-select (Claude-powered selection engine)
  const [autoSelectOpen, setAutoSelectOpen] = useState(false);
  const [autoSelectFlightId, setAutoSelectFlightId] = useState(null);
  const [autoSelectRunning, setAutoSelectRunning] = useState(false);
  const [autoSelectSteering, setAutoSelectSteering] = useState('');
  const [autoSelectOnlyEmpty, setAutoSelectOnlyEmpty] = useState(true);
  const [autoSelectError, setAutoSelectError] = useState('');
  const [autoSelectResult, setAutoSelectResult] = useState(null);
  const [selectionPromptId, setSelectionPromptId] = useState(null);
  const [persistentSteeringDraft, setPersistentSteeringDraft] = useState('');
  const [persistentSteeringOriginal, setPersistentSteeringOriginal] = useState('');
  const [persistentSteeringSaving, setPersistentSteeringSaving] = useState(false);
  const [persistentSteeringNotice, setPersistentSteeringNotice] = useState('');

  // Edit task dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState({});
  const [editAvatars, setEditAvatars] = useState([]);
  const [editPainPoints, setEditPainPoints] = useState([]);

  // Bulk select + apply
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [bulkApplyOpen, setBulkApplyOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkApplyFlightId, setBulkApplyFlightId] = useState(null);
  const [bulkForm, setBulkForm] = useState({ vertical: '', avatar: '', pain_point: '', style: '', audience: '', designer: '' });
  const [bulkAvatars, setBulkAvatars] = useState([]);
  const [bulkPainPoints, setBulkPainPoints] = useState([]);

  // Script generator
  const [scriptGenerating, setScriptGenerating] = useState(false);
  const [scriptHistory, setScriptHistory] = useState([]);
  const [scriptHistoryOpen, setScriptHistoryOpen] = useState(false);
  const [steeringPromptDraft, setSteeringPromptDraft] = useState('');
  const [thumbingScriptId, setThumbingScriptId] = useState(null);
  const currentScript = scriptHistory.find((s) => s.is_selected) || null;

  // Active Script inline editor — lets users tweak task.script_text after a winner is selected.
  // Edits live on CreativeTask only; the underlying CreativeTaskScript variant rows stay immutable.
  const [activeScriptEditing, setActiveScriptEditing] = useState(false);
  const [activeScriptDraft, setActiveScriptDraft] = useState('');
  const [activeScriptSaving, setActiveScriptSaving] = useState(false);

  // Custom-script paste — for tasks with is_custom_script=true, replaces the variants flow.
  const [customScriptDraft, setCustomScriptDraft] = useState('');
  const [customScriptSaving, setCustomScriptSaving] = useState(false);
  const [customScriptToggling, setCustomScriptToggling] = useState(false);

  // Inline editor for the ad / structured name override (custom_ad_name).
  const [adNameEditing, setAdNameEditing] = useState(false);
  const [adNameDraft, setAdNameDraft] = useState('');
  const [adNameSaving, setAdNameSaving] = useState(false);

  // Hook variant generator — multi-select hook types, N variants each
  const [variantsPanelOpen, setVariantsPanelOpen] = useState(false);
  const [variantHookSlugs, setVariantHookSlugs] = useState([]);
  const [variantCount, setVariantCount] = useState(1);
  const [variantsGenerating, setVariantsGenerating] = useState(false);
  const [variantsRunIds, setVariantsRunIds] = useState([]);  // ids from the last run, for the comparison view
  const [variantsErrors, setVariantsErrors] = useState([]);
  const [variantsRanking, setVariantsRanking] = useState(false);
  const [variantsTop3, setVariantsTop3] = useState([]);
  const [variantHooksCustomOpen, setVariantHooksCustomOpen] = useState(false);
  const [variantsAutoPickedHooks, setVariantsAutoPickedHooks] = useState([]);

  // Detail dialog
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Progressive-disclosure toggle — when off, the modal only shows what matters for the current task status
  const [showEverythingInDetail, setShowEverythingInDetail] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commenting, setCommenting] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [revisionText, setRevisionText] = useState('');
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitComment, setSubmitComment] = useState('');
  const [frameIoError, setFrameIoError] = useState('');

  // API modal
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [apiToken, setApiToken] = useState(null);
  const [apiTokenLoading, setApiTokenLoading] = useState(false);

  // Launch to Facebook
  const [fbLaunchOpen, setFbLaunchOpen] = useState(false);
  const [fbLaunchTask, setFbLaunchTask] = useState(null);
  const [fbStructure, setFbStructure] = useState([]);
  const [fbStructureLoading, setFbStructureLoading] = useState(false);
  const [fbLaunchForm, setFbLaunchForm] = useState({ ad_account_id: '', campaign_id: '', ad_set_ids: [], headline: '', primary_text: '', description: '', cta_type: 'LEARN_MORE', destination_url: '', display_link: '', page_id: '' });
  const [fbLaunchLoading, setFbLaunchLoading] = useState(false);
  const [fbLaunchResult, setFbLaunchResult] = useState(null);
  const [fbCopyGenerating, setFbCopyGenerating] = useState(false);
  const [fbHeadlineOptions, setFbHeadlineOptions] = useState([]);
  const [fbShowInactive, setFbShowInactive] = useState(false);
  const [approveRating, setApproveRating] = useState(0);
  const [ratingNote, setRatingNote] = useState('');
  const [ratingSaving, setRatingSaving] = useState(false);

  // Manage lookups
  const [manageOpen, setManageOpen] = useState(false);
  const [manageTab, setManageTab] = useState(0);
  const [manageVertical, setManageVertical] = useState('');
  const [manageAvatars, setManageAvatars] = useState([]);
  const [managePainPoints, setManagePainPoints] = useState([]);
  const [manageAngles, setManageAngles] = useState([]);
  const [manageHookTypes, setManageHookTypes] = useState([]);
  const [manageAdTypes, setManageAdTypes] = useState([]);
  const [manageStyles, setManageStyles] = useState([]);
  const [manageDesigners, setManageDesigners] = useState([]);
  const [manageGroups, setManageGroups] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageSaving, setManageSaving] = useState(false);
  const [manageError, setManageError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [scriptPrompt, setScriptPrompt] = useState(null);
  const [scriptPromptDraft, setScriptPromptDraft] = useState('');
  const [adCopyPrompt, setAdCopyPrompt] = useState(null);
  const [adCopyPromptDraft, setAdCopyPromptDraft] = useState('');
  const [judgePrompt, setJudgePrompt] = useState(null);
  const [judgePromptDraft, setJudgePromptDraft] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [designerDialogOpen, setDesignerDialogOpen] = useState(false);
  const [editingDesigner, setEditingDesigner] = useState(null);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
    addOption: addClientOption,
  } = useClientAutocomplete();

  // --- Load lookups on mount ---
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [vertRes, angleRes, hookTypeRes, adTypeRes, styleRes, designerRes, groupRes, ckRes] = await Promise.all([
          fetchVerticals(), listAngleChoices(), listHookTypeChoices(), listAdTypeChoices(), listStyleChoices(), listDesignerProfiles(), listDesignerGroups(), fetchClientCreativeKnowledge(),
        ]);
        const allVerticals = Array.isArray(vertRes.data) ? vertRes.data : [];
        const ckEntries = Array.isArray(ckRes.data) ? ckRes.data : [];
        const activeVerticalIds = new Set();
        ckEntries.forEach((entry) => {
          (entry.verticals || []).forEach((v) => { if (v.id) activeVerticalIds.add(v.id); });
        });
        setVerticals(activeVerticalIds.size > 0 ? allVerticals.filter((v) => activeVerticalIds.has(v.id)) : allVerticals);
        setAngles(Array.isArray(angleRes.data) ? angleRes.data : []);
        setHookTypes(Array.isArray(hookTypeRes.data) ? hookTypeRes.data : []);
        setAdTypes(Array.isArray(adTypeRes.data) ? adTypeRes.data : []);
        setStyles(Array.isArray(styleRes.data) ? styleRes.data : []);
        setDesigners(Array.isArray(designerRes.data) ? designerRes.data : []);
        setDesignerGroups(Array.isArray(groupRes.data) ? groupRes.data : []);
      } catch (_err) { /* keep usable */ }
    };
    loadLookups();
    loadFlights();
    loadPendingImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- External task imports ---
  const loadPendingImports = async () => {
    try {
      const res = await listTaskImports({ status: 'pending' });
      setPendingImportCount(Array.isArray(res.data) ? res.data.length : 0);
    } catch (_err) { /* non-critical */ }
  };

  const handleImportsChanged = () => {
    loadPendingImports();
    loadFlights();
    if (expandedFlightId) loadFlightTasks(expandedFlightId);
  };

  // Discard any in-progress Active Script edit when the detail dialog switches to a different task.
  useEffect(() => {
    setActiveScriptEditing(false);
    setActiveScriptDraft('');
    setCustomScriptDraft(detailTask?.script_text || '');
    setAdNameEditing(false);
    setAdNameDraft('');
  }, [detailTask?.id]);

  // --- Flight handlers ---
  const loadFlights = async () => {
    setFlightsLoading(true);
    setError('');
    try {
      const res = await listFlights({
        client_id: filterClient || undefined,
        status: filterStatus || undefined,
        designer_id: filterDesigner || undefined,
        source: filterSource || undefined,
        ...(showArchivedFlights ? { include_archived: 'true' } : {}),
      });
      const data = Array.isArray(res.data) ? res.data : [];
      data.forEach((f) => { if (f.client && f.client_name) addClientOption({ value: f.client, label: f.client_name }); });
      setFlights(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load flights'));
    } finally {
      setFlightsLoading(false);
    }
  };

  const loadFlightTasks = async (flightId) => {
    setFlightTasksLoading((prev) => ({ ...prev, [flightId]: true }));
    try {
      const res = await listCreativeTasks({ flight_id: flightId });
      const tasks = Array.isArray(res.data) ? res.data : [];
      setFlightTasks((prev) => ({ ...prev, [flightId]: tasks }));
      // Preload avatar/pain point options for all verticals used in these tasks
      const verticalIds = [...new Set(tasks.map((t) => t.vertical).filter(Boolean))];
      verticalIds.forEach((vId) => loadVerticalOptions(vId));
      // Preload client verticals for all clients in these tasks
      const clientIds = [...new Set(tasks.map((t) => t.client).filter(Boolean))];
      clientIds.forEach((cId) => loadClientVerticals(cId));
    } catch (_err) { /* keep usable */ }
    finally {
      setFlightTasksLoading((prev) => ({ ...prev, [flightId]: false }));
    }
  };

  const handleExpandFlight = (flightId) => {
    if (expandedFlightId === flightId) {
      setExpandedFlightId(null);
      return;
    }
    setExpandedFlightId(flightId);
    loadFlightTasks(flightId);
  };

  // --- Inline edit helpers ---
  const loadVerticalOptions = useCallback(async (verticalId) => {
    if (!verticalId || avatarsCacheRef.current[verticalId]) return;
    try {
      const [aRes, pRes] = await Promise.all([
        listAvatars({ vertical_id: verticalId }),
        listPainPoints({ vertical_id: verticalId }),
      ]);
      avatarsCacheRef.current[verticalId] = Array.isArray(aRes.data) ? aRes.data : [];
      painPointsCacheRef.current[verticalId] = Array.isArray(pRes.data) ? pRes.data : [];
      setVerticalOptionsVersion((v) => v + 1); // trigger re-render so rows pick up new options
    } catch (_err) { /* keep usable */ }
  }, []);

  const refreshTimerRef = useRef(null);
  const refreshFlightCountsQuietly = useCallback(() => {
    // Debounce — don't fire on every single cell change
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const res = await listFlights({
          client_id: filterClient || undefined,
          status: filterStatus || undefined,
          designer_id: filterDesigner || undefined,
          source: filterSource || undefined,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setFlights((prev) => {
          // Only update if counts actually changed to avoid unnecessary re-renders
          let changed = false;
          const next = prev.map((f) => {
            const u = data.find((d) => d.id === f.id);
            if (!u) return f;
            if (f.task_count !== u.task_count || f.completed_task_count !== u.completed_task_count || f.approved_task_count !== u.approved_task_count) {
              changed = true;
              return { ...f, task_count: u.task_count, completed_task_count: u.completed_task_count, approved_task_count: u.approved_task_count, status: u.status, status_display: u.status_display };
            }
            return f;
          });
          return changed ? next : prev;
        });
      } catch (_err) { /* silent */ }
    }, 1000);
  }, [filterClient, filterStatus, filterDesigner, filterSource, showArchivedFlights]);

  const handleInlineUpdate = async (taskId, flightId, field, value) => {
    const payload = { [field]: value || null };
    if (field === 'vertical') {
      payload.avatar = null;
      payload.pain_point = null;
      if (value) loadVerticalOptions(value);
    }
    if (field === 'client') {
      payload.vertical = null;
      payload.avatar = null;
      payload.pain_point = null;
      if (value) loadClientVerticals(value);
    }
    // Optimistic local update
    setFlightTasks((prev) => ({
      ...prev,
      [flightId]: (prev[flightId] || []).map((t) => (t.id === taskId ? { ...t, ...payload } : t)),
    }));
    try {
      const res = await updateCreativeTask(taskId, payload);
      // Merge only the computed fields from server response
      if (res.data) {
        setFlightTasks((prev) => {
          const tasks = prev[flightId] || [];
          const idx = tasks.findIndex((t) => t.id === taskId);
          if (idx === -1) return prev;
          const merged = { ...tasks[idx], ...res.data };
          // Skip update if nothing meaningful changed
          if (tasks[idx].is_complete === merged.is_complete && tasks[idx].status === merged.status && tasks[idx].structured_name === merged.structured_name) {
            return prev;
          }
          const next = [...tasks];
          next[idx] = merged;
          return { ...prev, [flightId]: next };
        });
      }
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update task'));
      loadFlightTasks(flightId);
    }
  };

  const handleInlineHookUpdate = async (taskId, flightId, hookText, hookSlug) => {
    setFlightTasks((prev) => ({
      ...prev,
      [flightId]: (prev[flightId] || []).map((t) => (t.id === taskId ? { ...t, hook_text: hookText, hook_slug: hookSlug } : t)),
    }));
    try {
      const res = await updateCreativeTask(taskId, { hook_text: hookText, hook_slug: hookSlug });
      if (res.data) {
        setFlightTasks((prev) => ({
          ...prev,
          [flightId]: (prev[flightId] || []).map((t) => (t.id === taskId ? { ...t, ...res.data } : t)),
        }));
      }
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update hook'));
      loadFlightTasks(flightId);
    }
  };

  // --- Bulk select + apply ---
  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      next.has(taskId) ? next.delete(taskId) : next.add(taskId);
      return next;
    });
  };

  const toggleAllInFlight = (flightId) => {
    const tasks = flightTasks[flightId] || [];
    const editableTasks = tasks.filter((t) => ['draft', 'assigned'].includes(t.status));
    const allSelected = editableTasks.every((t) => selectedTaskIds.has(t.id));
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      editableTasks.forEach((t) => allSelected ? next.delete(t.id) : next.add(t.id));
      return next;
    });
  };

  const openBulkApply = (flightId) => {
    setBulkApplyFlightId(flightId);
    setBulkForm({ vertical: '', avatar: '', pain_point: '', style: '', audience: '', designer: '' });
    setBulkAvatars([]);
    setBulkPainPoints([]);
    setBulkApplyOpen(true);
  };

  const handleBulkVerticalChange = async (verticalId) => {
    setBulkForm((p) => ({ ...p, vertical: verticalId, avatar: '', pain_point: '' }));
    if (verticalId) {
      try {
        const [aR, pR] = await Promise.all([listAvatars({ vertical_id: verticalId }), listPainPoints({ vertical_id: verticalId })]);
        setBulkAvatars(Array.isArray(aR.data) ? aR.data : []);
        setBulkPainPoints(Array.isArray(pR.data) ? pR.data : []);
      } catch (_err) { /* keep usable */ }
    } else { setBulkAvatars([]); setBulkPainPoints([]); }
  };

  const handleBulkApply = async () => {
    const ids = [...selectedTaskIds];
    if (!ids.length) return;
    const payload = { task_ids: ids };
    Object.entries(bulkForm).forEach(([k, v]) => { if (v) payload[k] = v; });
    if (Object.keys(payload).length <= 1) return; // only task_ids, no fields
    setBulkApplying(true);
    try {
      await bulkUpdateTasks(payload);
      setBulkApplyOpen(false);
      setSelectedTaskIds(new Set());
      if (bulkApplyFlightId) await loadFlightTasks(bulkApplyFlightId);
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to bulk update'));
    } finally {
      setBulkApplying(false);
    }
  };

  const handleDeleteTask = async (task, flightId) => {
    if (!window.confirm(`Delete ${task.task_code || 'this task'}? This cannot be undone.`)) return;
    try {
      await deleteCreativeTask(task.id);
      setSelectedTaskIds((prev) => { const next = new Set(prev); next.delete(task.id); return next; });
      await loadFlightTasks(flightId);
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete task'));
    }
  };

  const handleDeleteSelected = async (flightId) => {
    const tasks = flightTasks[flightId] || [];
    const ids = tasks.filter((t) => selectedTaskIds.has(t.id)).map((t) => t.id);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected task${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      await Promise.all(ids.map((id) => deleteCreativeTask(id)));
      setSelectedTaskIds((prev) => { const next = new Set(prev); ids.forEach((id) => next.delete(id)); return next; });
      await loadFlightTasks(flightId);
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete selected tasks'));
    }
  };

  // --- Script generator + feedback loop ---
  const reloadScriptHistory = useCallback(async (taskId) => {
    if (!taskId) return;
    try {
      const res = await listTaskScripts(taskId);
      setScriptHistory(Array.isArray(res.data) ? res.data : []);
    } catch (_err) { setScriptHistory([]); }
  }, []);

  const handleGenerateScript = async (taskId) => {
    setScriptGenerating(true);
    try {
      const payload = steeringPromptDraft.trim() ? { steering_prompt: steeringPromptDraft.trim() } : {};
      await generateScript(taskId, payload);
      const res = await fetchCreativeTask(taskId);
      setDetailTask(res.data);
      await reloadScriptHistory(taskId);
      setSteeringPromptDraft('');
      if (expandedFlightId) loadFlightTasks(expandedFlightId);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to generate script'));
    } finally {
      setScriptGenerating(false);
    }
  };

  const handleThumbScript = async (scriptId, thumb) => {
    if (!detailTask) return;
    setThumbingScriptId(scriptId);
    try {
      await thumbTaskScript(detailTask.id, scriptId, { thumb });
      await reloadScriptHistory(detailTask.id);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save thumb'));
    } finally {
      setThumbingScriptId(null);
    }
  };

  const handleSelectScript = async (scriptId) => {
    if (!detailTask) return;
    try {
      await selectTaskScript(detailTask.id, scriptId);
      const [taskRes] = await Promise.all([
        fetchCreativeTask(detailTask.id),
        reloadScriptHistory(detailTask.id),
      ]);
      setDetailTask(taskRes.data);
      // Switching variants overwrites task.script_text on the backend; drop any
      // in-progress edit so the UI shows the freshly-selected variant.
      setActiveScriptEditing(false);
      setActiveScriptDraft('');
      if (expandedFlightId) loadFlightTasks(expandedFlightId);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to switch script'));
    }
  };

  const handleEditActiveScript = () => {
    setActiveScriptDraft(detailTask?.script_text || '');
    setActiveScriptEditing(true);
  };

  const handleCancelActiveScript = () => {
    setActiveScriptEditing(false);
    setActiveScriptDraft('');
  };

  const handleToggleIsCustomScript = async (checked) => {
    if (!detailTask) return;
    setCustomScriptToggling(true);
    try {
      const res = await updateCreativeTask(detailTask.id, { is_custom_script: checked });
      setDetailTask((prev) => (prev ? { ...prev, ...(res.data || {}), is_custom_script: checked } : prev));
      if (expandedFlightId) {
        setFlightTasks((prev) => ({
          ...prev,
          [expandedFlightId]: (prev[expandedFlightId] || []).map((t) =>
            t.id === detailTask.id ? { ...t, ...(res.data || {}), is_custom_script: checked } : t
          ),
        }));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to toggle custom script mode'));
    } finally {
      setCustomScriptToggling(false);
    }
  };

  // Toggle is_custom_script from the table row (optimistic, no detail modal required).
  const handleRowToggleCustomScript = async (taskId, flightId, checked) => {
    setFlightTasks((prev) => ({
      ...prev,
      [flightId]: (prev[flightId] || []).map((t) => (t.id === taskId ? { ...t, is_custom_script: checked } : t)),
    }));
    try {
      await updateCreativeTask(taskId, { is_custom_script: checked });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to toggle custom script mode'));
      loadFlightTasks(flightId);
    }
  };

  const handleSaveAdName = async () => {
    if (!detailTask) return;
    const next = adNameDraft.trim();
    // No-op if unchanged (compare against the override, not the rendered/auto name)
    if (next === (detailTask.custom_ad_name || '')) {
      setAdNameEditing(false);
      return;
    }
    setAdNameSaving(true);
    try {
      const res = await updateCreativeTask(detailTask.id, { custom_ad_name: next });
      setDetailTask((prev) => (prev ? { ...prev, ...(res.data || {}) } : prev));
      if (expandedFlightId) {
        setFlightTasks((prev) => ({
          ...prev,
          [expandedFlightId]: (prev[expandedFlightId] || []).map((t) =>
            t.id === detailTask.id ? { ...t, ...(res.data || {}) } : t
          ),
        }));
      }
      setAdNameEditing(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save ad name'));
    } finally {
      setAdNameSaving(false);
    }
  };

  const handleSaveCustomScript = async () => {
    if (!detailTask) return;
    setCustomScriptSaving(true);
    try {
      const res = await updateCreativeTask(detailTask.id, { script_text: customScriptDraft });
      setDetailTask((prev) => (prev ? { ...prev, ...(res.data || {}), script_text: customScriptDraft } : prev));
      if (expandedFlightId) {
        setFlightTasks((prev) => ({
          ...prev,
          [expandedFlightId]: (prev[expandedFlightId] || []).map((t) =>
            t.id === detailTask.id ? { ...t, ...(res.data || {}), script_text: customScriptDraft } : t
          ),
        }));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save custom script'));
    } finally {
      setCustomScriptSaving(false);
    }
  };

  const handleSaveActiveScript = async () => {
    if (!detailTask) return;
    setActiveScriptSaving(true);
    try {
      const res = await updateCreativeTask(detailTask.id, { script_text: activeScriptDraft });
      setDetailTask((prev) => (prev ? { ...prev, ...(res.data || {}), script_text: activeScriptDraft } : prev));
      if (expandedFlightId) {
        setFlightTasks((prev) => ({
          ...prev,
          [expandedFlightId]: (prev[expandedFlightId] || []).map((t) =>
            t.id === detailTask.id ? { ...t, ...(res.data || {}), script_text: activeScriptDraft } : t
          ),
        }));
      }
      setActiveScriptEditing(false);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save script'));
    } finally {
      setActiveScriptSaving(false);
    }
  };

  const handleGenerateVariants = async () => {
    if (!detailTask) return;
    setVariantsGenerating(true);
    setVariantsErrors([]);
    setVariantsTop3([]);
    setVariantsAutoPickedHooks([]);
    let createdIds = [];
    try {
      const res = await generateScriptVariants(detailTask.id, {
        // Empty hook_slugs lets the backend auto-pick 3 via Claude.
        hook_slugs: variantHookSlugs,
        count: variantCount,
        ...(steeringPromptDraft.trim() ? { steering_prompt: steeringPromptDraft.trim() } : {}),
      });
      const created = Array.isArray(res.data?.created) ? res.data.created : [];
      createdIds = created.map((s) => s.id);
      setVariantsRunIds(createdIds);
      setVariantsErrors(Array.isArray(res.data?.errors) ? res.data.errors : []);
      if (res.data?.auto_picked && Array.isArray(res.data?.hook_slugs)) {
        setVariantsAutoPickedHooks(res.data.hook_slugs);
      }
      await reloadScriptHistory(detailTask.id);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to generate variants'));
      setVariantsGenerating(false);
      return;
    }
    setVariantsGenerating(false);

    // Auto-rank — second AI pass runs as soon as variants land, no extra click.
    if (createdIds.length >= 2) {
      setVariantsRanking(true);
      try {
        const rankRes = await rankScriptVariants(detailTask.id, { script_ids: createdIds });
        console.log('[auto-rank] response:', rankRes.data);
        setVariantsTop3(Array.isArray(rankRes.data?.top_3) ? rankRes.data.top_3 : []);
        await reloadScriptHistory(detailTask.id);
      } catch (err) {
        console.error('[auto-rank] error:', err?.response?.data || err);
        const detail = err?.response?.data?.detail || '';
        const raw = err?.response?.data?.raw || '';
        // Don't blow up the whole flow — variants are still usable without ranking.
        setError(`Variants generated, but ranking failed: ${detail}${raw ? ` — judge said: ${raw.slice(0, 200)}…` : ''}`);
      } finally {
        setVariantsRanking(false);
      }
    }
  };

  const toggleVariantHookSlug = (slug) => {
    setVariantHookSlugs((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]);
  };

  const handleRankVariants = async () => {
    if (!detailTask || variantsRunIds.length < 2) return;
    setVariantsRanking(true);
    try {
      const res = await rankScriptVariants(detailTask.id, { script_ids: variantsRunIds });
      // Surface the raw judge response in the console so we can debug parser misses.
      console.log('[rank-variants] response:', res.data);
      setVariantsTop3(Array.isArray(res.data?.top_3) ? res.data.top_3 : []);
      await reloadScriptHistory(detailTask.id);
    } catch (err) {
      console.error('[rank-variants] error:', err?.response?.data || err);
      const detail = err?.response?.data?.detail || '';
      const raw = err?.response?.data?.raw || '';
      setError(detail + (raw ? ` — judge said: ${raw.slice(0, 200)}…` : '') || 'Failed to rank variants');
    } finally {
      setVariantsRanking(false);
    }
  };

  const validSegments = (segs) => segs.filter((s) => s.client && s.vertical && s.count > 0);
  const totalSegmentCount = (segs) => validSegments(segs).reduce((sum, s) => sum + (parseInt(s.count, 10) || 0), 0);

  const handleCreateFlight = async () => {
    setCreatingFlight(true);
    setCreateFlightError('');
    try {
      // Create the flight
      const flightRes = await createFlight({
        name: flightForm.name,
        designer: flightForm.designer || null,
        designer_group: flightForm.designer_group || null,
        start_date: flightForm.start_date,
        end_date: flightForm.end_date,
        notes: flightForm.notes,
        target_count: totalSegmentCount(flightSegments),
      });
      const flightId = flightRes.data.id;

      // Generate tasks from segments
      const segs = validSegments(flightSegments);
      if (segs.length) {
        await generateFlightTasks(flightId, {
          segments: segs.map((s) => ({ client: s.client, vertical: s.vertical, count: parseInt(s.count, 10) })),
        });
      }

      setCreateFlightOpen(false);
      setFlightForm({ name: '', designer: '', start_date: '', end_date: '', notes: '' });
      setFlightSegments([{ client: '', vertical: '', count: 5 }]);
      await loadFlights();
      setExpandedFlightId(flightId);
      loadFlightTasks(flightId);
    } catch (err) {
      setCreateFlightError(getApiErrorMessage(err, 'Failed to create flight'));
    } finally {
      setCreatingFlight(false);
    }
  };

  const handleGenerateTasks = async () => {
    if (!generateFlightId) return;
    setGeneratingTasks(true);
    setError('');
    try {
      const segs = validSegments(generateSegments);
      if (!segs.length) return;
      await generateFlightTasks(generateFlightId, {
        segments: segs.map((s) => ({
          client: s.client,
          vertical: s.vertical,
          count: parseInt(s.count, 10),
          is_custom_script: !!s.is_custom_script,
        })),
      });
      setGenerateOpen(false);
      setGenerateSegments([{ client: '', vertical: '', count: 5 }]);
      await loadFlights();
      await loadFlightTasks(generateFlightId);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to generate tasks'));
    } finally {
      setGeneratingTasks(false);
    }
  };

  const openAutoSelect = async (flightId) => {
    setAutoSelectFlightId(flightId);
    setAutoSelectSteering('');
    setAutoSelectOnlyEmpty(true);
    setAutoSelectError('');
    setAutoSelectResult(null);
    setPersistentSteeringNotice('');
    setAutoSelectOpen(true);
    try {
      const res = await listSelectionPrompts();
      const rows = Array.isArray(res.data) ? res.data : [];
      const def = rows.find((r) => r.is_default) || rows[0] || null;
      if (def) {
        setSelectionPromptId(def.id);
        const steering = def.default_steering_prompt || '';
        setPersistentSteeringDraft(steering);
        setPersistentSteeringOriginal(steering);
      } else {
        setSelectionPromptId(null);
        setPersistentSteeringDraft('');
        setPersistentSteeringOriginal('');
        setPersistentSteeringNotice('No default SelectionPromptTemplate found — run migrations.');
      }
    } catch (err) {
      setSelectionPromptId(null);
      setPersistentSteeringDraft('');
      setPersistentSteeringOriginal('');
      setPersistentSteeringNotice(getApiErrorMessage(err, 'Failed to load persistent steering (is the migration applied?)'));
    }
  };

  const handleSavePersistentSteering = async () => {
    if (!selectionPromptId) return;
    setPersistentSteeringSaving(true);
    setPersistentSteeringNotice('');
    try {
      await updateSelectionPrompt(selectionPromptId, { default_steering_prompt: persistentSteeringDraft });
      setPersistentSteeringOriginal(persistentSteeringDraft);
      setPersistentSteeringNotice('Saved — applies to every future run.');
    } catch (err) {
      setPersistentSteeringNotice(getApiErrorMessage(err, 'Failed to save persistent steering'));
    } finally {
      setPersistentSteeringSaving(false);
    }
  };

  const handleAutoSelect = async () => {
    if (!autoSelectFlightId) return;
    setAutoSelectRunning(true);
    setAutoSelectError('');
    setAutoSelectResult(null);
    try {
      const res = await autoSelectFlight(autoSelectFlightId, {
        steering_prompt: autoSelectSteering,
        only_empty: autoSelectOnlyEmpty,
      });
      setAutoSelectResult(res.data);
      await loadFlightTasks(autoSelectFlightId);
    } catch (err) {
      setAutoSelectError(getApiErrorMessage(err, 'Selection engine failed'));
    } finally {
      setAutoSelectRunning(false);
    }
  };

  // --- Task handlers ---
  const openDetail = async (taskId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setCommentText('');
    setScriptHistory([]);
    setScriptHistoryOpen(false);
    setSteeringPromptDraft('');
    // Reset variants UI between tasks; auto-expand the accordion if the task has no script yet.
    setVariantsRunIds([]);
    setVariantsTop3([]);
    setVariantsErrors([]);
    setVariantsAutoPickedHooks([]);
    setVariantHookSlugs([]);
    setVariantHooksCustomOpen(false);
    try {
      const res = await fetchCreativeTask(taskId);
      setDetailTask(res.data);
      setVariantsPanelOpen(!res.data.script_text);
      setApproveRating(res.data.rating || 0);
      setRatingNote(res.data.rating_note || '');
      reloadScriptHistory(taskId);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load task'));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // Deep-link support: /creative-tasks?task=<id> opens that task's detail
  // dialog (used by Designer Creative Performance to jump from an ad row
  // back to its flight task). The param is consumed once, then cleared.
  useEffect(() => {
    const taskParam = searchParams.get('task');
    if (!taskParam) return;
    const taskId = Number(taskParam);
    const next = new URLSearchParams(searchParams);
    next.delete('task');
    setSearchParams(next, { replace: true });
    if (Number.isFinite(taskId) && taskId > 0) openDetail(taskId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAction = async (actionFn, taskId, label) => {
    setActionLoading(label);
    try {
      const res = await actionFn(taskId);
      setDetailTask(res.data);
      if (expandedFlightId) await loadFlightTasks(expandedFlightId);
      await loadFlights();
    } catch (err) {
      setError(getApiErrorMessage(err, `Failed to ${label}`));
    } finally {
      setActionLoading('');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !detailTask) return;
    setCommenting(true);
    try {
      await addCreativeTaskComment(detailTask.id, { body: commentText.trim() });
      const res = await fetchCreativeTask(detailTask.id);
      setDetailTask(res.data);
      setCommentText('');
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to add comment')); }
    finally { setCommenting(false); }
  };

  const handleSubmitTask = async () => {
    if (!detailTask) return;
    setActionLoading('submit');
    try {
      // Ensure frame_io_url is saved before submitting
      const currentUrl = (detailTask.frame_io_url || '').trim();
      if (currentUrl && currentUrl !== (detailTask._saved_frame_io_url || '')) {
        await updateCreativeTask(detailTask.id, { frame_io_url: currentUrl });
      }
      const res = await submitCreativeTask(detailTask.id, {
        comment: submitComment.trim() || undefined,
      });
      setDetailTask(res.data);
      setSubmitOpen(false);
      setSubmitComment('');
      if (expandedFlightId) loadFlightTasks(expandedFlightId);
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to submit'));
    } finally {
      setActionLoading('');
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionText.trim() || !detailTask) return;
    setActionLoading('revision');
    try {
      const res = await requestRevisionCreativeTask(detailTask.id, { body: revisionText.trim() });
      setDetailTask(res.data);
      setRevisionOpen(false);
      setRevisionText('');
      if (expandedFlightId) await loadFlightTasks(expandedFlightId);
      await loadFlights();
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to request revision')); }
    finally { setActionLoading(''); }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !detailTask) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'deliverable');
    setActionLoading('upload');
    try {
      await uploadCreativeTaskFile(detailTask.id, formData);
      const res = await fetchCreativeTask(detailTask.id);
      setDetailTask(res.data);
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to upload file')); }
    finally { setActionLoading(''); event.target.value = ''; }
  };

  // --- Edit task ---
  const openEditTask = async (task) => {
    setEditForm({
      id: task.id,
      client: task.client,
      vertical: task.vertical || '',
      avatar: task.avatar || '',
      pain_point: task.pain_point || '',
      hook_text: task.hook_text || '',
      hook_slug: task.hook_slug || '',
      ad_type: task.ad_type || '',
      style: task.style || '',
      audience: task.audience || 'all',
      designer: task.designer || '',
      script_text: task.script_text || '',
      frame_io_url: task.frame_io_url || '',
      notes: task.notes || '',
      default_steering_prompt: task.default_steering_prompt || '',
    });
    setEditError('');
    setEditOpen(true);
    if (task.vertical) {
      try {
        const [avatarRes, painRes] = await Promise.all([
          listAvatars({ vertical_id: task.vertical }),
          listPainPoints({ vertical_id: task.vertical }),
        ]);
        setEditAvatars(Array.isArray(avatarRes.data) ? avatarRes.data : []);
        setEditPainPoints(Array.isArray(painRes.data) ? painRes.data : []);
      } catch (_err) { /* keep usable */ }
    }
  };

  const handleEditVerticalChange = async (verticalId) => {
    setEditForm((prev) => ({ ...prev, vertical: verticalId, avatar: '', pain_point: '' }));
    if (verticalId) {
      try {
        const [avatarRes, painRes] = await Promise.all([
          listAvatars({ vertical_id: verticalId }), listPainPoints({ vertical_id: verticalId }),
        ]);
        setEditAvatars(Array.isArray(avatarRes.data) ? avatarRes.data : []);
        setEditPainPoints(Array.isArray(painRes.data) ? painRes.data : []);
      } catch (_err) { /* keep usable */ }
    } else { setEditAvatars([]); setEditPainPoints([]); }
  };

  const handleSaveEdit = async () => {
    setEditing(true);
    setEditError('');
    try {
      const { id, ...payload } = editForm;
      await updateCreativeTask(id, payload);
      setEditOpen(false);
      if (detailTask && detailTask.id === id) {
        const res = await fetchCreativeTask(id);
        setDetailTask(res.data);
      }
      if (expandedFlightId) await loadFlightTasks(expandedFlightId);
      await loadFlights();
    } catch (err) { setEditError(getApiErrorMessage(err, 'Failed to update task')); }
    finally { setEditing(false); }
  };

  // --- Manage lookups handlers (kept from before) ---
  const loadManageVerticalLookups = async (verticalId, archived) => {
    if (!verticalId) { setManageAvatars([]); setManagePainPoints([]); return; }
    setManageLoading(true);
    setManageError('');
    try {
      const params = { vertical_id: verticalId, ...(archived ? { include_archived: 'true' } : {}) };
      const [avatarRes, painRes] = await Promise.all([listAvatars(params), listPainPoints(params)]);
      setManageAvatars(Array.isArray(avatarRes.data) ? avatarRes.data : []);
      setManagePainPoints(Array.isArray(painRes.data) ? painRes.data : []);
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to load lookup data')); }
    finally { setManageLoading(false); }
  };

  const loadManageGlobalLookups = async (archived) => {
    setManageLoading(true);
    setManageError('');
    try {
      const params = archived ? { include_archived: 'true' } : {};
      const [angleRes, hookTypeRes, adTypeRes, styleRes, designerRes, groupRes, usersRes, promptRes, adCopyPromptRes, judgePromptRes] = await Promise.all([
        listAngleChoices(params), listHookTypeChoices(params), listAdTypeChoices(params), listStyleChoices(params),
        listDesignerProfiles(params), listDesignerGroups(params), listAssignmentUsers(), listScriptPrompts(), listAdCopyPrompts(), listSelectionPrompts(),
      ]);
      setManageAngles(Array.isArray(angleRes.data) ? angleRes.data : []);
      setManageHookTypes(Array.isArray(hookTypeRes.data) ? hookTypeRes.data : []);
      setManageAdTypes(Array.isArray(adTypeRes.data) ? adTypeRes.data : []);
      setManageStyles(Array.isArray(styleRes.data) ? styleRes.data : []);
      setManageDesigners(Array.isArray(designerRes.data) ? designerRes.data : []);
      setManageGroups(Array.isArray(groupRes.data) ? groupRes.data : []);
      setStaffUsers(Array.isArray(usersRes.data) ? usersRes.data : Array.isArray(usersRes.data?.results) ? usersRes.data.results : []);
      const prompts = Array.isArray(promptRes.data) ? promptRes.data : [];
      const defaultPrompt = prompts.find((p) => p.is_default) || prompts[0] || null;
      setScriptPrompt(defaultPrompt);
      setScriptPromptDraft(defaultPrompt?.system_prompt || '');
      const adCopyPrompts = Array.isArray(adCopyPromptRes.data) ? adCopyPromptRes.data : [];
      const defaultAdCopyPrompt = adCopyPrompts.find((p) => p.is_default) || adCopyPrompts[0] || null;
      setAdCopyPrompt(defaultAdCopyPrompt);
      setAdCopyPromptDraft(defaultAdCopyPrompt?.system_prompt || '');
      const judgePrompts = Array.isArray(judgePromptRes.data) ? judgePromptRes.data : [];
      const defaultJudgePrompt = judgePrompts.find((p) => p.is_default) || judgePrompts[0] || null;
      setJudgePrompt(defaultJudgePrompt);
      setJudgePromptDraft(defaultJudgePrompt?.system_prompt || '');
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to load lookup data')); }
    finally { setManageLoading(false); }
  };

  const handleOpenManage = () => {
    setManageOpen(true); setManageTab(0); setManageError('');
    loadManageGlobalLookups(showArchived);
    if (manageVertical) loadManageVerticalLookups(manageVertical, showArchived);
  };
  const handleManageVerticalChange = (v) => { setManageVertical(v); loadManageVerticalLookups(v, showArchived); };
  const handleToggleShowArchived = () => {
    const next = !showArchived; setShowArchived(next);
    loadManageGlobalLookups(next);
    if (manageVertical) loadManageVerticalLookups(manageVertical, next);
  };

  const openEditDialog = (type, item = null) => {
    const nameField = type === 'avatar' ? 'name' : 'label';
    setEditingItem(item
      ? { type, id: item.id, name: item[nameField] || item.name || '', slug: item.slug, vertical: item.vertical, description: item.description || '', reference_url: item.reference_url || '', priority: item.priority ?? 50 }
      : { type, id: null, name: '', slug: '', vertical: ['angle', 'hook_type', 'style', 'ad_type'].includes(type) ? null : manageVertical, description: '', reference_url: '', priority: 50 }
    );
    setEditDialogOpen(true); setManageError('');
  };

  const handleSaveItem = async () => {
    if (!editingItem || !editingItem.name.trim() || !editingItem.slug.trim()) return;
    setManageSaving(true); setManageError('');
    try {
      const { type, id, name, slug, vertical, description, reference_url } = editingItem;
      if (type === 'avatar') { const p = { name: name.trim(), slug: slug.trim(), vertical, description: (description || '').trim() }; id ? await updateAvatar(id, p) : await createAvatar(p); }
      else if (type === 'pain_point') { const p = { label: name.trim(), slug: slug.trim(), vertical, description: (description || '').trim() }; id ? await updatePainPoint(id, p) : await createPainPoint(p); }
      else if (type === 'angle') { const p = { label: name.trim(), slug: slug.trim() }; id ? await updateAngleChoice(id, p) : await createAngleChoice(p); }
      else if (type === 'hook_type') { const p = { label: name.trim(), slug: slug.trim(), priority: Number.isFinite(Number(editingItem.priority)) ? Number(editingItem.priority) : 50 }; id ? await updateHookTypeChoice(id, p) : await createHookTypeChoice(p); }
      else if (type === 'ad_type') { const p = { label: name.trim(), slug: slug.trim() }; id ? await updateAdTypeChoice(id, p) : await createAdTypeChoice(p); }
      else if (type === 'style') { const p = { label: name.trim(), slug: slug.trim(), description: (description || '').trim(), reference_url: (reference_url || '').trim() }; id ? await updateStyleChoice(id, p) : await createStyleChoice(p); }
      else if (type === 'group') { const p = { name: name.trim(), slug: slug.trim(), members: editingItem.members || [] }; id ? await updateDesignerGroup(id, p) : await createDesignerGroup(p); }
      setEditDialogOpen(false); setEditingItem(null);
      if (['angle', 'hook_type', 'style', 'ad_type', 'group'].includes(type)) {
        if (type === 'group') { const gR = await listDesignerGroups(); setDesignerGroups(Array.isArray(gR.data) ? gR.data : []); }
        await loadManageGlobalLookups(showArchived);
        const [aR, htR, atR, sR] = await Promise.all([listAngleChoices(), listHookTypeChoices(), listAdTypeChoices(), listStyleChoices()]);
        setAngles(Array.isArray(aR.data) ? aR.data : []); setHookTypes(Array.isArray(htR.data) ? htR.data : []); setAdTypes(Array.isArray(atR.data) ? atR.data : []); setStyles(Array.isArray(sR.data) ? sR.data : []);
      } else { await loadManageVerticalLookups(manageVertical, showArchived); }
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to save')); }
    finally { setManageSaving(false); }
  };

  const handleToggleActive = async (type, id, currentActive) => {
    setManageSaving(true); setManageError('');
    try {
      const p = { active: !currentActive };
      if (type === 'avatar') await updateAvatar(id, p);
      else if (type === 'pain_point') await updatePainPoint(id, p);
      else if (type === 'angle') await updateAngleChoice(id, p);
      else if (type === 'hook_type') await updateHookTypeChoice(id, p);
      else if (type === 'ad_type') await updateAdTypeChoice(id, p);
      else if (type === 'style') await updateStyleChoice(id, p);
      if (['angle', 'hook_type', 'style', 'ad_type'].includes(type)) {
        await loadManageGlobalLookups(showArchived);
        const [aR, htR, atR, sR] = await Promise.all([listAngleChoices(), listHookTypeChoices(), listAdTypeChoices(), listStyleChoices()]);
        setAngles(Array.isArray(aR.data) ? aR.data : []); setHookTypes(Array.isArray(htR.data) ? htR.data : []); setAdTypes(Array.isArray(atR.data) ? atR.data : []); setStyles(Array.isArray(sR.data) ? sR.data : []);
      } else { await loadManageVerticalLookups(manageVertical, showArchived); }
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to update')); }
    finally { setManageSaving(false); }
  };

  const openDesignerDialog = (d = null) => {
    setEditingDesigner(d ? { id: d.id, user: d.user, display_name: d.display_name || '', slug: d.slug || '' } : { id: null, user: '', display_name: '', slug: '' });
    setDesignerDialogOpen(true); setManageError('');
  };
  const handleSaveDesigner = async () => {
    if (!editingDesigner?.user || !editingDesigner?.slug?.trim()) return;
    setManageSaving(true); setManageError('');
    try {
      const p = { user: editingDesigner.user, display_name: editingDesigner.display_name.trim(), slug: editingDesigner.slug.trim() };
      editingDesigner.id ? await updateDesignerProfile(editingDesigner.id, p) : await createDesignerProfile(p);
      setDesignerDialogOpen(false); setEditingDesigner(null);
      await loadManageGlobalLookups(showArchived);
      const res = await listDesignerProfiles(); setDesigners(Array.isArray(res.data) ? res.data : []);
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to save designer')); }
    finally { setManageSaving(false); }
  };
  const handleToggleDesignerActive = async (id, active) => {
    setManageSaving(true); setManageError('');
    try {
      await updateDesignerProfile(id, { active: !active });
      await loadManageGlobalLookups(showArchived);
      const res = await listDesignerProfiles(); setDesigners(Array.isArray(res.data) ? res.data : []);
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to update')); }
    finally { setManageSaving(false); }
  };

  const handleSavePrompt = async () => {
    if (!scriptPrompt || !scriptPromptDraft.trim()) return;
    setManageSaving(true);
    setManageError('');
    try {
      await updateScriptPrompt(scriptPrompt.id, { system_prompt: scriptPromptDraft });
      setScriptPrompt((prev) => ({ ...prev, system_prompt: scriptPromptDraft }));
    } catch (err) {
      setManageError(getApiErrorMessage(err, 'Failed to save prompt'));
    } finally {
      setManageSaving(false);
    }
  };

  const handleSaveAdCopyPrompt = async () => {
    if (!adCopyPrompt || !adCopyPromptDraft.trim()) return;
    setManageSaving(true);
    setManageError('');
    try {
      await updateAdCopyPrompt(adCopyPrompt.id, { system_prompt: adCopyPromptDraft });
      setAdCopyPrompt((prev) => ({ ...prev, system_prompt: adCopyPromptDraft }));
    } catch (err) {
      setManageError(getApiErrorMessage(err, 'Failed to save ad copy prompt'));
    } finally {
      setManageSaving(false);
    }
  };

  const handleSaveJudgePrompt = async () => {
    if (!judgePrompt || !judgePromptDraft.trim()) return;
    setManageSaving(true);
    setManageError('');
    try {
      await updateSelectionPrompt(judgePrompt.id, { system_prompt: judgePromptDraft });
      setJudgePrompt((prev) => ({ ...prev, system_prompt: judgePromptDraft }));
    } catch (err) {
      setManageError(getApiErrorMessage(err, 'Failed to save judge prompt'));
    } finally {
      setManageSaving(false);
    }
  };

  // Helper to render a lookup table for the manage dialog
  const renderLookupTable = (items, type, nameKey = 'label') => (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="flex-end">
        <Button size="small" startIcon={<AddIcon />} onClick={() => openEditDialog(type)}>
          Add {{ avatar: 'Avatar', pain_point: 'Pain Point', angle: 'Angle', ad_type: 'Ad Type', style: 'Style' }[type]}
        </Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{nameKey === 'name' ? 'Name' : 'Label'}</TableCell>
              <TableCell>Slug</TableCell>
              {type === 'hook_type' ? <TableCell>Priority</TableCell> : null}
              {type === 'style' ? <TableCell>Description</TableCell> : null}
              {type === 'style' ? <TableCell>Example</TableCell> : null}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={type === 'style' ? 5 : type === 'hook_type' ? 4 : 3} align="center">None found.</TableCell></TableRow>
            ) : items.map((item) => (
              <TableRow key={item.id} hover sx={!item.active ? { opacity: 0.5 } : {}}>
                <TableCell>{item[nameKey]}{!item.active ? <Chip label="Archived" size="small" sx={{ ml: 1, height: 18 }} /> : null}</TableCell>
                <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{item.slug}</Typography></TableCell>
                {type === 'hook_type' ? (
                  <TableCell>
                    <Chip size="small" label={item.priority ?? 50} color={(item.priority ?? 50) <= 30 ? 'success' : (item.priority ?? 50) >= 70 ? 'default' : 'primary'} variant={(item.priority ?? 50) <= 30 ? 'filled' : 'outlined'} sx={{ height: 20, fontFamily: 'monospace' }} />
                  </TableCell>
                ) : null}
                {type === 'style' ? (
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.description || '—'}
                    </Typography>
                  </TableCell>
                ) : null}
                {type === 'style' ? (
                  <TableCell>
                    {item.reference_url ? (
                      <IconButton size="small" component="a" href={item.reference_url} target="_blank" rel="noopener noreferrer" title="Open example">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                ) : null}
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEditDialog(type, item)}><EditIcon fontSize="small" /></IconButton>
                  <Button size="small" sx={{ minWidth: 0, ml: 0.5, fontSize: '0.7rem' }} color={item.active ? 'warning' : 'success'} disabled={manageSaving} onClick={() => handleToggleActive(type, item.id, item.active)}>
                    {item.active ? 'Archive' : 'Restore'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );

  return (
    <Box sx={{ px: 2, py: 2 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Typography variant="h5" fontWeight={700}>Creative Flights</Typography>
          {designerBanks.map((bank) => {
            const balance = Number(bank.balance) || 0;
            const remaining = bank.ads_remaining;
            const label = `${designerBanks.length > 1 ? `${bank.name}: ` : 'Bank: '}$${balance.toLocaleString('en-US', { maximumFractionDigits: 0 })}${
              remaining === null || remaining === undefined ? '' : ` · ${remaining} ads left`
            }`;
            return (
              <Chip
                key={bank.id}
                size="small"
                {...(isStaff ? { clickable: true, component: RouterLink, to: '/designer-bank' } : {})}
                color={bank.is_low || balance <= 0 ? 'error' : 'default'}
                variant={bank.is_low || balance <= 0 ? 'filled' : 'outlined'}
                label={label}
              />
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadFlights} disabled={flightsLoading}>Refresh</Button>
          {isStaff ? (
            <FormControlLabel
              control={<Switch size="small" checked={showArchivedFlights} onChange={(e) => { setShowArchivedFlights(e.target.checked); }} />}
              label={<Typography variant="caption">Archived</Typography>}
            />
          ) : null}
          <Button variant="outlined" color="inherit" startIcon={<ApiIcon />} onClick={() => setApiModalOpen(true)}>API</Button>
          {isStaff ? (
            <Button
              variant={pendingImportCount > 0 ? 'contained' : 'outlined'}
              color="warning"
              startIcon={<CloudUploadIcon />}
              onClick={() => setImportModalOpen(true)}
            >
              {pendingImportCount > 0 ? `Review Imports (${pendingImportCount})` : 'Imports'}
            </Button>
          ) : null}
          {isStaff ? <Button variant="outlined" startIcon={<SettingsIcon />} onClick={handleOpenManage}>Manage Lookups</Button> : null}
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setCustomScriptOpen(true)}>Custom Script</Button>
          {isStaff ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateFlightOpen(true)}>Create Flight</Button> : null}
        </Stack>
      </Stack>

      {/* Pending imports banner */}
      {isStaff && pendingImportCount > 0 ? (
        <Alert
          severity="warning"
          icon={<CloudUploadIcon />}
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => setImportModalOpen(true)}>Review</Button>}
        >
          {pendingImportCount} imported task batch{pendingImportCount > 1 ? 'es are' : ' is'} waiting for review before going live.
        </Alert>
      ) : null}

      {/* Filters */}
      <Stack direction="row" spacing={1.5} mb={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Autocomplete
          size="small" sx={{ minWidth: 200 }}
          options={clientOptions} loading={clientOptionsLoading}
          value={findClientOptionByValue(filterClient) || null}
          onInputChange={(_e, v) => handleClientSearch(v)}
          onChange={(_e, v) => setFilterClient(v?.value || '')}
          renderInput={(params) => <TextField {...params} label="Client" placeholder="All" />}
        />
        <TextField size="small" select label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="in_progress">In Progress</MenuItem>
          <MenuItem value="complete">Complete</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
        {isStaff ? (
          <TextField size="small" select label="Designer" value={filterDesigner} onChange={(e) => setFilterDesigner(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            {designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}
          </TextField>
        ) : null}
        {isStaff ? (
          <TextField size="small" select label="Source" value={filterSource} onChange={(e) => setFilterSource(e.target.value)} sx={{ minWidth: 150 }} helperText="Legacy = weekly flights">
            <MenuItem value="">Legacy</MenuItem>
            <MenuItem value="concept_cell">Creative Engine</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        ) : null}
        <Button size="small" variant="outlined" onClick={loadFlights} disabled={flightsLoading}>Apply</Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {/* Flights list */}
      {flightsLoading ? (
        <Stack alignItems="center" py={6}><CircularProgress /></Stack>
      ) : flights.length === 0 ? (
        <Card variant="outlined"><CardContent><Typography align="center" color="text.secondary">No flights found.</Typography></CardContent></Card>
      ) : (
        <Stack spacing={1}>
          {(() => {
            const grouped = { active: [], draft: [], complete: [], archived: [] };
            for (const f of flights) grouped[flightGroupKey(f)].push(f);
            const renderFlightCard = (flight) => {
            const progress = flight.target_count > 0 ? Math.round((flight.completed_task_count / flight.target_count) * 100) : 0;
            const isExpanded = expandedFlightId === flight.id;
            const tasks = flightTasks[flight.id] || [];
            const tasksLoading = flightTasksLoading[flight.id] || false;
            const due = flightDueState(flight);
            const flightHasNoDesigner = !flight.designer_name && !flight.designer_group_name;
            const needsRevisionCount = tasks.filter((t) => t.status === 'needs_revision').length;

            return (
              <Accordion key={flight.id} expanded={isExpanded} onChange={() => handleExpandFlight(flight.id)} variant="outlined" disableGutters>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': { my: 1 },
                    ...(due.kind === 'overdue' ? { borderLeft: '3px solid', borderColor: 'error.main' } : {}),
                    ...(due.kind === 'due_soon' ? { borderLeft: '3px solid', borderColor: 'warning.main' } : {}),
                  }}
                >
                  <Stack spacing={0.5} sx={{ width: '100%', pr: 2 }}>
                    {/* Top line — identity + status signals */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography fontWeight={700} sx={{ ...(flight.archived ? { opacity: 0.5, textDecoration: 'line-through' } : {}) }}>
                        {flight.flight_code ? <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 1 }}>{flight.flight_code}</Box> : null}
                        {flight.name}
                      </Typography>
                      <Chip label={flight.status_display} color={FLIGHT_STATUS_COLORS[flight.status] || 'default'} size="small" sx={{ height: 22 }} />
                      {flight.archived ? <Chip label="Archived" size="small" variant="outlined" sx={{ height: 22 }} /> : null}
                      {due.kind === 'overdue' ? (
                        <Chip
                          icon={<WarningAmberIcon sx={{ fontSize: '0.9rem !important' }} />}
                          label={`${due.days}d overdue`}
                          size="small"
                          color="error"
                          sx={{ height: 22, fontWeight: 700 }}
                        />
                      ) : null}
                      {due.kind === 'due_soon' ? (
                        <Chip
                          label={due.days === 0 ? 'Due today' : `Due in ${due.days}d`}
                          size="small"
                          color="warning"
                          sx={{ height: 22, fontWeight: 700 }}
                        />
                      ) : null}
                      {flightHasNoDesigner ? (
                        <Chip icon={<PersonAddIcon sx={{ fontSize: '0.9rem !important' }} />} label="No designer" size="small" variant="outlined" color="warning" sx={{ height: 22 }} />
                      ) : null}
                      {needsRevisionCount > 0 ? (
                        <Chip label={`${needsRevisionCount} needs edits`} size="small" sx={{ height: 22, ...STATUS_CHIP_SX.needs_revision }} />
                      ) : null}
                      {flight.approved_task_count > 0 ? (
                        <Chip label={`${flight.approved_task_count} approved`} color="success" size="small" sx={{ height: 22 }} />
                      ) : null}
                    </Stack>
                    {/* Bottom line — context + progress */}
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                      {flight.client_name ? (
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>{flight.client_name}</Typography>
                      ) : null}
                      <Typography variant="caption" color={flightHasNoDesigner ? 'warning.dark' : 'text.secondary'}>
                        {flight.designer_name || 'No designer'}
                      </Typography>
                      {flight.start_date && flight.end_date ? (
                        <Typography
                          variant="caption"
                          color={due.kind === 'overdue' ? 'error.main' : (due.kind === 'due_soon' ? 'warning.dark' : 'text.secondary')}
                          fontWeight={due.kind === 'overdue' || due.kind === 'due_soon' ? 700 : 400}
                        >
                          {flight.start_date} → {flight.end_date}
                        </Typography>
                      ) : null}
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 150, maxWidth: 360 }}>
                        <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                        <Typography variant="caption" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{flight.completed_task_count}/{flight.target_count}</Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Status summary pills */}
                  {tasks.length > 0 ? (() => {
                    const counts = {};
                    tasks.forEach((t) => { counts[t.status] = (counts[t.status] || 0) + 1; });
                    const ORDER = [
                      { key: 'assigned', label: 'Assigned' },
                      { key: 'in_progress', label: 'In Progress' },
                      { key: 'submitted', label: 'Submitted' },
                      { key: 'needs_revision', label: 'Needs Edits' },
                      { key: 'approved', label: 'Approved' },
                      { key: 'draft', label: 'Draft' },
                    ];
                    return (
                      <Stack direction="row" spacing={0.75} mb={1.5} flexWrap="wrap" useFlexGap>
                        {ORDER.filter((s) => counts[s.key]).map((s) => (
                          <Chip key={s.key} label={`${counts[s.key]} ${s.label}`} size="small" sx={{ height: 22, fontWeight: 600, fontSize: '0.7rem', ...(STATUS_CHIP_SX[s.key] || {}) }} />
                        ))}
                        <Chip label={`${tasks.length} Total`} size="small" variant="outlined" sx={{ height: 22, fontWeight: 600, fontSize: '0.7rem' }} />
                      </Stack>
                    );
                  })() : null}
                  {/* Flight actions */}
                  {isStaff ? (
                    <Stack direction="row" spacing={1} mb={2} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => { setGenerateFlightId(flight.id); setGenerateOpen(true); }}>
                        Generate Tasks
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} onClick={() => openAutoSelect(flight.id)}>
                        Auto-Fill Selections
                      </Button>
                      {selectedTaskIds.size > 0 && tasks.some((t) => selectedTaskIds.has(t.id)) ? (
                        <>
                          <Button size="small" variant="outlined" onClick={() => openBulkApply(flight.id)}>
                            Apply to {[...selectedTaskIds].filter((id) => tasks.some((t) => t.id === id)).length} selected
                          </Button>
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => handleDeleteSelected(flight.id)}>
                            Delete {[...selectedTaskIds].filter((id) => tasks.some((t) => t.id === id)).length} selected
                          </Button>
                        </>
                      ) : null}
                      <Box sx={{ flex: 1 }} />
                      <Button size="small" color="inherit" startIcon={<DownloadIcon />} onClick={async () => {
                        try {
                          const res = await exportFlightTasks(flight.id);
                          const blob = new Blob([res.data], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${(flight.name || 'flight').replace(/\s+/g, '_')}_tasks.csv`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (_e) { /* silent */ }
                      }}>
                        Export CSV
                      </Button>
                      <Button size="small" color={flight.archived ? 'primary' : 'inherit'} onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await archiveFlight(flight.id);
                          loadFlights();
                        } catch (_e) { /* silent */ }
                      }}>
                        {flight.archived ? 'Unarchive' : 'Archive'}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} mb={2} justifyContent="flex-end">
                      <Button size="small" color="inherit" startIcon={<DownloadIcon />} onClick={async () => {
                        try {
                          const res = await exportFlightTasks(flight.id);
                          const blob = new Blob([res.data], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${(flight.name || 'flight').replace(/\s+/g, '_')}_tasks.csv`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (_e) { /* silent */ }
                      }}>
                        Export CSV
                      </Button>
                    </Stack>
                  )}

                  {/* Tasks table */}
                  {tasksLoading ? (
                    <Stack alignItems="center" py={3}><CircularProgress size={24} /></Stack>
                  ) : tasks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" py={2}>
                      {flight?.source === 'concept_cell'
                        ? "No client tasks yet — deploy this master to its lane from the Creative Engine briefs page."
                        : 'No tasks yet. Generate tasks to get started.'}
                    </Typography>
                  ) : flight?.source === 'concept_cell' ? (
                    <TableContainer>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.75 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 70 }}>Code</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>Offer</TableCell>
                            <TableCell>Master / Cell</TableCell>
                            <TableCell>Designer</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Frame.io</TableCell>
                            <TableCell sx={{ width: 90 }}>Updated</TableCell>
                            <TableCell align="right" sx={{ width: 50 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tasks.map((task, idx) => {
                            const lm = task.lane_master || {};
                            return (
                              <TableRow key={task.id} hover>
                                <TableCell><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{task.task_code || `#${idx + 1}`}</Typography></TableCell>
                                <TableCell><Typography variant="body2" fontWeight={600}>{task.client_name || '—'}</Typography></TableCell>
                                <TableCell><Typography variant="caption">{lm.offer || '—'}</Typography></TableCell>
                                <TableCell>
                                  <Typography variant="caption" fontWeight={600} display="block">
                                    {task.vertical_name ? `${task.vertical_name} · ` : ''}{lm.concept_name || ''} · {lm.brief_code || ''}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {lm.cell_name ? `Style: ${lm.cell_name}` : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell><Typography variant="caption">{task.designer_name || '—'}</Typography></TableCell>
                                <TableCell><Chip label={task.status_display} size="small" sx={{ height: 20, ...(STATUS_CHIP_SX[task.status] || STATUS_CHIP_SX.draft) }} /></TableCell>
                                <TableCell>
                                  {task.frame_io_url ? (
                                    <Typography variant="caption" component="a" href={task.frame_io_url} target="_blank" rel="noopener" sx={{ color: 'primary.main' }}>link</Typography>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">—</Typography>
                                  )}
                                </TableCell>
                                <TableCell><Typography variant="caption" color="text.secondary">{task.updated_at ? new Date(task.updated_at).toLocaleDateString() : ''}</Typography></TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" onClick={() => openDetail(task.id)}><VisibilityIcon fontSize="small" /></IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <TableContainer>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.75 }, '& .MuiSelect-select': { py: 0.5, fontSize: '0.75rem' }, '& .MuiInputBase-root': { fontSize: '0.75rem' } }}>
                        <TableHead>
                          <TableRow>
                            {isStaff ? (
                              <TableCell sx={{ width: 40 }}>
                                <Checkbox
                                  size="small"
                                  checked={tasks.filter((t) => ['draft', 'assigned'].includes(t.status)).length > 0 && tasks.filter((t) => ['draft', 'assigned'].includes(t.status)).every((t) => selectedTaskIds.has(t.id))}
                                  indeterminate={tasks.filter((t) => ['draft', 'assigned'].includes(t.status)).some((t) => selectedTaskIds.has(t.id)) && !tasks.filter((t) => ['draft', 'assigned'].includes(t.status)).every((t) => selectedTaskIds.has(t.id))}
                                  onChange={() => toggleAllInFlight(flight.id)}
                                />
                              </TableCell>
                            ) : null}
                            <TableCell sx={{ width: 50 }}>Code</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>Vertical</TableCell>
                            <TableCell>Audience</TableCell>
                            <TableCell>Style</TableCell>
                            <TableCell>Avatar</TableCell>
                            <TableCell>Pain Point</TableCell>
                            <TableCell>Designer</TableCell>
                            <TableCell sx={{ width: 90 }} align="center">
                              <Tooltip title="Progress: script / Frame.io / files"><span>Progress</span></Tooltip>
                            </TableCell>
                            <TableCell sx={{ width: 70 }}>Updated</TableCell>
                            <TableCell align="right" sx={{ width: 60 }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tasks.map((task, idx) => {
                            const rowAvatars = avatarsCacheRef.current[task.vertical] || [];
                            const rowPainPoints = painPointsCacheRef.current[task.vertical] || [];
                            const canInlineEdit = isStaff && ['draft', 'assigned'].includes(task.status);

                            // Audience-aware sorting — prioritize relevant avatars/pain points
                            const audiencePriority = {
                              '55+': ['safety', 'care', 'peace', 'lthome', 'comfort', 'prevent', 'protect', 'proud'],
                              'women': ['bmom', 'family', 'fashion', 'org', 'embarr', 'care', 'comfort'],
                              'men': ['garage', 'carent', 'diybout', 'storm', 'insure', 'wkndwar', 'protect'],
                            };
                            const boostSlugs = new Set(audiencePriority[task.audience] || []);
                            const sortByAudience = (items) => boostSlugs.size === 0 ? items : [...items].sort((a, b) => {
                              const aBoost = boostSlugs.has(a.slug) ? 0 : 1;
                              const bBoost = boostSlugs.has(b.slug) ? 0 : 1;
                              return aBoost - bBoost;
                            });
                            const sortedAvatars = sortByAudience(rowAvatars);
                            const sortedPainPoints = (() => {
                              const painPriority = {
                                '55+': ['slip', 'mobility', 'safety', 'leak', 'oldroof', 'cold', 'crack'],
                                'women': ['clean', 'mold', 'embarr', 'clutter', 'morning', 'dated'],
                                'men': ['uglygr', 'crack', 'leak', 'stormdmg', 'nrgloss', 'stain'],
                              };
                              const painBoost = new Set(painPriority[task.audience] || []);
                              return painBoost.size === 0 ? rowPainPoints : [...rowPainPoints].sort((a, b) => {
                                const aB = painBoost.has(a.slug) ? 0 : 1;
                                const bB = painBoost.has(b.slug) ? 0 : 1;
                                return aB - bB;
                              });
                            })();

                            return (
                              <TableRow key={task.id} hover sx={{ ...(!task.is_complete ? { bgcolor: 'action.hover' } : {}), ...(selectedTaskIds.has(task.id) ? { bgcolor: 'primary.50' } : {}) }}>
                                {isStaff ? (
                                  <TableCell>
                                    <Checkbox size="small" checked={selectedTaskIds.has(task.id)} onChange={() => toggleTaskSelection(task.id)} disabled={!['draft', 'assigned'].includes(task.status)} />
                                  </TableCell>
                                ) : null}
                                <TableCell><Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>{task.task_code || `#${idx + 1}`}</Typography></TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Chip label={task.status_display} size="small" sx={{ height: 20, ...(STATUS_CHIP_SX[task.status] || STATUS_CHIP_SX.draft) }} />
                                    {task.revision_round > 0 ? (
                                      <Chip
                                        label={`R${task.revision_round}`}
                                        size="small"
                                        variant="outlined"
                                        title={`${task.revision_round} revision round${task.revision_round === 1 ? '' : 's'}`}
                                        sx={{ height: 18, fontSize: '0.65rem', borderColor: 'warning.light', color: 'warning.dark' }}
                                      />
                                    ) : null}
                                    <Chip
                                      label={task.is_custom_script ? 'Custom' : '+ Custom'}
                                      size="small"
                                      variant={task.is_custom_script ? 'filled' : 'outlined'}
                                      color={task.is_custom_script ? 'secondary' : 'default'}
                                      onClick={() => handleRowToggleCustomScript(task.id, flight.id, !task.is_custom_script)}
                                      title={task.is_custom_script ? 'Click to switch back to AI variants flow' : 'Click to switch this task to custom-script (paste-your-own) mode'}
                                      sx={{ height: 18, fontSize: '0.65rem', opacity: task.is_custom_script ? 1 : 0.55, cursor: 'pointer' }}
                                    />
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <Autocomplete
                                      size="small"
                                      disableClearable
                                      options={clientOptions}
                                      loading={clientOptionsLoading}
                                      value={findClientOptionByValue(task.client) || (task.client ? { value: task.client, label: task.client_name || '' } : null)}
                                      onInputChange={(_e, v, reason) => { if (reason === 'input') handleClientSearch(v); }}
                                      onChange={(_e, v) => { const cid = v?.value || ''; if (cid && cid !== task.client) handleInlineUpdate(task.id, flight.id, 'client', cid); }}
                                      isOptionEqualToValue={(o, v) => o?.value === v?.value}
                                      sx={{ minWidth: 130, maxWidth: 160 }}
                                      renderInput={(params) => <TextField {...params} variant="standard" InputProps={{ ...params.InputProps, disableUnderline: true }} />}
                                    />
                                  ) : <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>{task.client_name || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.vertical || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'vertical', e.target.value)} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.vertical }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {getVerticalsForClient(task.client).map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.vertical_name || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.audience || 'all'} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'audience', e.target.value)} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: true }}>
                                      {AUDIENCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{AUDIENCE_OPTIONS.find((o) => o.value === task.audience)?.label || 'All Ages'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.style || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'style', e.target.value)} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.style }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {styles.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.style_label || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.avatar || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'avatar', e.target.value)} disabled={!task.vertical} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.avatar }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {sortedAvatars.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.avatar_name || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.pain_point || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'pain_point', e.target.value)} disabled={!task.vertical} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.pain_point }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {sortedPainPoints.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.pain_point_label || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.designer_group ? `g:${task.designer_group}` : (task.designer || '')} onChange={async (e) => {
                                      const val = e.target.value;
                                      let patch;
                                      if (typeof val === 'string' && val.startsWith('g:')) {
                                        patch = { designer_group: Number(val.slice(2)), designer: null };
                                      } else {
                                        patch = { designer: val || null, designer_group: null };
                                      }
                                      try {
                                        await updateCreativeTask(task.id, patch);
                                        setFlightTasks((prev) => ({ ...prev, [flight.id]: (prev[flight.id] || []).map((t) => t.id === task.id ? { ...t, ...patch } : t) }));
                                      } catch (_e) { /* silent */ }
                                    }} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !(task.designer || task.designer_group) }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {designerGroups.length > 0 ? <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', py: 0.25 }}>Groups</MenuItem> : null}
                                      {designerGroups.map((g) => <MenuItem key={`g:${g.id}`} value={`g:${g.id}`}>{g.name} ({g.member_count})</MenuItem>)}
                                      {designerGroups.length > 0 && designers.length > 0 ? <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', py: 0.25 }}>Individuals</MenuItem> : null}
                                      {designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.designer_name || '—'}</Typography>}
                                </TableCell>
                                <TableCell align="center">
                                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                    <Tooltip title={task.script_text ? 'Script generated' : 'No script yet'}>
                                      <AutoFixHighIcon
                                        fontSize="inherit"
                                        sx={{ fontSize: '1rem', color: task.script_text ? 'primary.main' : 'action.disabled' }}
                                      />
                                    </Tooltip>
                                    <Tooltip title={task.frame_io_url ? 'Frame.io link set' : 'No Frame.io link'}>
                                      {task.frame_io_url ? (
                                        <IconButton
                                          size="small"
                                          component="a"
                                          href={task.frame_io_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          sx={{ p: 0.25 }}
                                        >
                                          <OpenInNewIcon sx={{ fontSize: '0.9rem', color: 'info.main' }} />
                                        </IconButton>
                                      ) : (
                                        <OpenInNewIcon sx={{ fontSize: '0.9rem', color: 'action.disabled' }} />
                                      )}
                                    </Tooltip>
                                    <Tooltip title={task.file_count ? `${task.file_count} file${task.file_count === 1 ? '' : 's'} uploaded` : 'No files'}>
                                      {task.file_count > 0 ? (
                                        <Chip
                                          label={task.file_count}
                                          size="small"
                                          color="success"
                                          sx={{ height: 16, fontSize: '0.65rem', fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }}
                                        />
                                      ) : (
                                        <Box component="span" sx={{ fontSize: '0.7rem', color: 'action.disabled' }}>0</Box>
                                      )}
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {task.updated_at ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      title={new Date(task.updated_at).toLocaleString()}
                                      sx={{ fontSize: '0.65rem' }}
                                    >
                                      {formatRelativeTime(task.updated_at)}
                                    </Typography>
                                  ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                                </TableCell>
                                <TableCell align="right">
                                  {isStaff ? <IconButton size="small" onClick={() => openEditTask(task)} title="Full edit"><EditIcon fontSize="small" /></IconButton> : null}
                                  <IconButton size="small" onClick={() => openDetail(task.id)} title="View"><VisibilityIcon fontSize="small" /></IconButton>
                                  {isStaff && ['draft', 'assigned'].includes(task.status) ? (
                                    <IconButton size="small" color="error" onClick={() => handleDeleteTask(task, flight.id)} title="Delete"><DeleteOutlineIcon fontSize="small" /></IconButton>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            );
            };
            return (
              <Stack spacing={2}>
                {FLIGHT_GROUPS.map((group) => {
                  const items = grouped[group.key] || [];
                  if (items.length === 0) return null;
                  const collapsed = collapsedFlightGroups[group.key];
                  return (
                    <Box key={group.key}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 0.75, cursor: 'pointer', userSelect: 'none', opacity: group.key === 'archived' || group.key === 'complete' ? 0.7 : 1 }}
                        onClick={() => setCollapsedFlightGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                      >
                        <ExpandMoreIcon fontSize="small" sx={{ transition: 'transform 150ms', transform: collapsed ? 'rotate(-90deg)' : 'none' }} />
                        <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
                          {group.label}
                        </Typography>
                        <Chip label={items.length} size="small" sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700 }} />
                      </Stack>
                      {!collapsed ? (
                        <Stack spacing={1}>
                          {items.map((f) => renderFlightCard(f))}
                        </Stack>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            );
          })()}
        </Stack>
      )}

      {/* ========== CREATE FLIGHT DIALOG ========== */}
      <Dialog open={createFlightOpen} onClose={() => setCreateFlightOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Flight</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {createFlightError ? <Alert severity="error">{createFlightError}</Alert> : null}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Flight Name" required fullWidth value={flightForm.name} onChange={(e) => setFlightForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Week of March 24" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select fullWidth label="Assign To" value={flightForm.designer_group ? `g:${flightForm.designer_group}` : (flightForm.designer || '')} onChange={(e) => {
                  const val = e.target.value;
                  if (typeof val === 'string' && val.startsWith('g:')) {
                    setFlightForm((p) => ({ ...p, designer_group: Number(val.slice(2)), designer: '' }));
                  } else {
                    setFlightForm((p) => ({ ...p, designer: val, designer_group: '' }));
                  }
                }}>
                  <MenuItem value="">None</MenuItem>
                  {designerGroups.length > 0 ? <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', py: 0.25 }}>Groups</MenuItem> : null}
                  {designerGroups.map((g) => <MenuItem key={`g:${g.id}`} value={`g:${g.id}`}>{g.name} ({g.member_count})</MenuItem>)}
                  {designerGroups.length > 0 && designers.length > 0 ? <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', py: 0.25 }}>Individuals</MenuItem> : null}
                  {designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}><TextField label="Start Date" type="date" fullWidth required InputLabelProps={{ shrink: true }} value={flightForm.start_date} onChange={(e) => setFlightForm((p) => ({ ...p, start_date: e.target.value }))} /></Grid>
              <Grid item xs={6} md={3}><TextField label="End Date" type="date" fullWidth required InputLabelProps={{ shrink: true }} value={flightForm.end_date} onChange={(e) => setFlightForm((p) => ({ ...p, end_date: e.target.value }))} /></Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Notes" fullWidth value={flightForm.notes} onChange={(e) => setFlightForm((p) => ({ ...p, notes: e.target.value }))} />
              </Grid>
            </Grid>

            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>Ad Segments ({totalSegmentCount(flightSegments)} total ads)</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setFlightSegments((p) => [...p, { client: '', vertical: '', count: 5 }])}>
                Add Segment
              </Button>
            </Stack>

            {flightSegments.map((seg, idx) => (
              <Card key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Autocomplete
                      size="small"
                      options={clientOptions} loading={clientOptionsLoading}
                      value={findClientOptionByValue(seg.client) || null}
                      onInputChange={(_e, v) => handleClientSearch(v)}
                      onChange={(_e, v) => { const cid = v?.value || ''; if (cid) loadClientVerticals(cid); setFlightSegments((p) => p.map((s, i) => i === idx ? { ...s, client: cid, vertical: '' } : s)); }}
                      renderInput={(params) => <TextField {...params} label="Client" required />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField size="small" select fullWidth label="Vertical" required value={seg.vertical} onChange={(e) => setFlightSegments((p) => p.map((s, i) => i === idx ? { ...s, vertical: e.target.value } : s))} disabled={!seg.client}>
                      <MenuItem value="">Select vertical</MenuItem>
                      {getVerticalsForClient(seg.client).map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={8} md={2}>
                    <TextField size="small" label="# Ads" type="number" fullWidth required value={seg.count} onChange={(e) => setFlightSegments((p) => p.map((s, i) => i === idx ? { ...s, count: e.target.value } : s))} inputProps={{ min: 1, max: 200 }} />
                  </Grid>
                  <Grid item xs={4} md={1}>
                    {flightSegments.length > 1 ? (
                      <IconButton size="small" color="error" onClick={() => setFlightSegments((p) => p.filter((_, i) => i !== idx))}>
                        <Typography variant="caption" sx={{ fontSize: '1rem' }}>×</Typography>
                      </IconButton>
                    ) : null}
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFlightOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFlight} disabled={creatingFlight || !flightForm.name || !flightForm.start_date || !flightForm.end_date || totalSegmentCount(flightSegments) === 0}>
            {creatingFlight ? 'Creating...' : `Create Flight (${totalSegmentCount(flightSegments)} ads)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== GENERATE TASKS DIALOG (add to existing flight) ========== */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Tasks to Flight</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">Segments ({totalSegmentCount(generateSegments)} tasks)</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setGenerateSegments((p) => [...p, { client: '', vertical: '', count: 5 }])}>
                Add Segment
              </Button>
            </Stack>
            {generateSegments.map((seg, idx) => (
              <Card key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      size="small"
                      options={clientOptions} loading={clientOptionsLoading}
                      value={findClientOptionByValue(seg.client) || null}
                      onInputChange={(_e, v) => handleClientSearch(v)}
                      onChange={(_e, v) => { const cid = v?.value || ''; if (cid) loadClientVerticals(cid); setGenerateSegments((p) => p.map((s, i) => i === idx ? { ...s, client: cid, vertical: '' } : s)); }}
                      renderInput={(params) => <TextField {...params} label="Client" required />}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField size="small" select fullWidth label="Vertical" required value={seg.vertical} onChange={(e) => setGenerateSegments((p) => p.map((s, i) => i === idx ? { ...s, vertical: e.target.value } : s))} disabled={!seg.client}>
                      <MenuItem value="">Select vertical</MenuItem>
                      {getVerticalsForClient(seg.client).map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField size="small" label="# Ads" type="number" fullWidth required value={seg.count} onChange={(e) => setGenerateSegments((p) => p.map((s, i) => i === idx ? { ...s, count: e.target.value } : s))} inputProps={{ min: 1, max: 200 }} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={!!seg.is_custom_script}
                          onChange={(e) => setGenerateSegments((p) => p.map((s, i) => i === idx ? { ...s, is_custom_script: e.target.checked } : s))}
                        />
                      }
                      label={<Typography variant="caption">Custom script</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    {generateSegments.length > 1 ? (
                      <IconButton size="small" color="error" onClick={() => setGenerateSegments((p) => p.filter((_, i) => i !== idx))}>
                        <Typography variant="caption" sx={{ fontSize: '1rem' }}>×</Typography>
                      </IconButton>
                    ) : null}
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerateTasks} disabled={generatingTasks || totalSegmentCount(generateSegments) === 0}>
            {generatingTasks ? 'Generating...' : `Generate ${totalSegmentCount(generateSegments)} Tasks`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== AUTO-FILL SELECTIONS DIALOG ========== */}
      <Dialog open={autoSelectOpen} onClose={() => !autoSelectRunning && setAutoSelectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto-Fill Selections</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Claude will pick avatar, pain point, hook type, ad type, and style for each task in this flight,
              using client brand context, the task's audience, and recent approved combos to avoid repeats.
              Audience and vertical are never changed.
            </Typography>
            {autoSelectError ? <Alert severity="error">{autoSelectError}</Alert> : null}
            <FormControlLabel
              control={<Switch checked={autoSelectOnlyEmpty} onChange={(e) => setAutoSelectOnlyEmpty(e.target.checked)} disabled={autoSelectRunning} />}
              label={autoSelectOnlyEmpty ? 'Only fill empty fields (keep existing picks)' : 'Overwrite all six selection fields'}
            />
            <TextField
              label="Steering prompt (optional)"
              placeholder="e.g. lean into financing for 55+; avoid humor; emphasize storm-damage urgency"
              multiline
              minRows={3}
              value={autoSelectSteering}
              onChange={(e) => setAutoSelectSteering(e.target.value)}
              disabled={autoSelectRunning}
              fullWidth
            />
            {autoSelectResult ? (
              <Alert severity={autoSelectResult.parse_errors?.length ? 'warning' : 'success'}>
                Updated {autoSelectResult.updated_task_ids?.length || 0} task{autoSelectResult.updated_task_ids?.length === 1 ? '' : 's'} across {autoSelectResult.group_count || 0} client/vertical group{autoSelectResult.group_count === 1 ? '' : 's'}.
                {autoSelectResult.skipped_no_vertical?.length ? ` Skipped ${autoSelectResult.skipped_no_vertical.length} task(s) missing a vertical.` : ''}
                {autoSelectResult.parse_errors?.length ? ` ${autoSelectResult.parse_errors.length} group(s) failed to parse — try again.` : ''}
              </Alert>
            ) : null}
            <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <SettingsIcon fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight={600}>Persistent steering rules</Typography>
                  <Typography variant="caption" color="text.secondary">— applied to every run</Typography>
                  {persistentSteeringDraft !== persistentSteeringOriginal ? (
                    <Chip size="small" label="Unsaved" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                  ) : null}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    Durable business rules prepended to every selection run. Edit when policy changes
                    (e.g. format restrictions, client exclusions). Leave the per-run box below for one-off nudges.
                  </Typography>
                  <TextField
                    placeholder="e.g. Only choose video ad_types — we are not producing static ads right now."
                    multiline
                    minRows={5}
                    value={persistentSteeringDraft}
                    onChange={(e) => setPersistentSteeringDraft(e.target.value)}
                    disabled={persistentSteeringSaving}
                    fullWidth
                    helperText={!selectionPromptId ? 'Loaded in read-only mode — saving requires a valid default template (run migrations).' : ' '}
                  />
                  {persistentSteeringNotice ? (
                    <Alert severity={persistentSteeringNotice.startsWith('Saved') ? 'success' : 'error'} sx={{ py: 0 }}>
                      {persistentSteeringNotice}
                    </Alert>
                  ) : null}
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      onClick={() => setPersistentSteeringDraft(persistentSteeringOriginal)}
                      disabled={persistentSteeringSaving || persistentSteeringDraft === persistentSteeringOriginal}
                    >
                      Reset
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSavePersistentSteering}
                      disabled={persistentSteeringSaving || !selectionPromptId || persistentSteeringDraft === persistentSteeringOriginal}
                    >
                      {persistentSteeringSaving ? 'Saving…' : 'Save rules'}
                    </Button>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoSelectOpen(false)} disabled={autoSelectRunning}>Close</Button>
          <Button variant="contained" startIcon={<AutoFixHighIcon />} onClick={handleAutoSelect} disabled={autoSelectRunning}>
            {autoSelectRunning ? 'Running…' : 'Run Selection Engine'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== EDIT TASK DIALOG ========== */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {editError ? <Alert severity="error">{editError}</Alert> : null}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Vertical" value={editForm.vertical || ''} onChange={(e) => handleEditVerticalChange(e.target.value)}><MenuItem value="">—</MenuItem>{verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Designer" value={editForm.designer || ''} onChange={(e) => setEditForm((p) => ({ ...p, designer: e.target.value }))}><MenuItem value="">—</MenuItem>{designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}</TextField></Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Avatar" value={editForm.avatar || ''} onChange={(e) => setEditForm((p) => ({ ...p, avatar: e.target.value }))} disabled={!editForm.vertical}><MenuItem value="">—</MenuItem>{editAvatars.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Pain Point" value={editForm.pain_point || ''} onChange={(e) => setEditForm((p) => ({ ...p, pain_point: e.target.value }))} disabled={!editForm.vertical}><MenuItem value="">—</MenuItem>{editPainPoints.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}</TextField></Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField select fullWidth label="Style" value={editForm.style || ''} onChange={(e) => setEditForm((p) => ({ ...p, style: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {styles.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                </TextField>
                {editForm.style ? (() => {
                  const sel = styles.find((s) => s.id === editForm.style);
                  if (!sel) return null;
                  return (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                      {sel.description ? <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>{sel.description}</Typography> : null}
                      {sel.reference_url ? <Button size="small" component="a" href={sel.reference_url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon fontSize="small" />}>Example</Button> : null}
                    </Stack>
                  );
                })() : null}
              </Grid>
            </Grid>
            <TextField label="Steering Prompt (default for this task)" fullWidth multiline minRows={2} value={editForm.default_steering_prompt || ''} onChange={(e) => setEditForm((p) => ({ ...p, default_steering_prompt: e.target.value }))} placeholder="e.g. 'lean into urgency about active leaks; avoid mentioning specific dollar amounts'" helperText="Applied every time you generate or regenerate the script for this task." />
            <TextField select fullWidth label="Audience" value={editForm.audience || 'all'} onChange={(e) => setEditForm((p) => ({ ...p, audience: e.target.value }))}>
              {AUDIENCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField label="Hook Description" fullWidth multiline minRows={2} value={editForm.hook_text || ''} onChange={(e) => setEditForm((p) => ({ ...p, hook_text: e.target.value }))} />
            <TextField label="Hook Slug" fullWidth value={editForm.hook_slug || ''} onChange={(e) => setEditForm((p) => ({ ...p, hook_slug: e.target.value.slice(0, 20) }))} helperText={`${(editForm.hook_slug || '').length}/20`} InputProps={{ sx: { fontFamily: 'monospace' } }} />
            {editForm.script_text ? (
              <TextField
                label="Ad Script"
                fullWidth multiline minRows={6}
                value={editForm.script_text || ''}
                onChange={(e) => setEditForm((p) => ({ ...p, script_text: e.target.value }))}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            ) : null}
            <TextField label="Frame.io URL" fullWidth type="url" value={editForm.frame_io_url || ''} onChange={(e) => setEditForm((p) => ({ ...p, frame_io_url: e.target.value }))} placeholder="https://app.frame.io/..." />
            <TextField label="Notes" multiline minRows={2} fullWidth value={editForm.notes || ''} onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editing}>{editing ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ========== DETAIL DIALOG ========== */}
      <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setDetailTask(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ flexGrow: 1 }}>
              {detailTask?.task_code ? `${detailTask.task_code} — Task Details` : 'Task Details'}
            </Box>
            {detailTask && !detailTask.lane_brief ? (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={!!detailTask.is_custom_script}
                      onChange={(e) => handleToggleIsCustomScript(e.target.checked)}
                      disabled={customScriptToggling}
                    />
                  }
                  label={<Typography variant="caption">Custom script</Typography>}
                  sx={{ mr: 0 }}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={showEverythingInDetail} onChange={(e) => setShowEverythingInDetail(e.target.checked)} />}
                  label={<Typography variant="caption">Show everything</Typography>}
                  sx={{ mr: 0 }}
                />
              </>
            ) : null}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detailTask ? (
            <Stack alignItems="center" py={4}><CircularProgress /></Stack>
          ) : (() => {
            // ---- Progressive-disclosure focus stage ----
            // Drives which sections of the modal are visible. The "Show everything"
            // toggle flips back to the full long-scroll layout for power users.
            // Concept × Cell tasks carry NO legacy avatar/pain-point/style
            // dimensions and no generated script — the linked brief markdown
            // IS the spec. Treat fields/script as satisfied so the stage
            // follows status (assigned→produce→submit→review→approve) instead
            // of getting stuck on the irrelevant "fill in fields / generate
            // variants" setup stages.
            const isConceptCell = !!detailTask.lane_brief;
            // Custom-script tasks bypass the avatar/pain/hook/style gate AND
            // the script-must-exist gate — the operator (or the assigned
            // designer) pastes the script whenever; assignment doesn't wait
            // on it.
            const fieldsReady = isConceptCell || detailTask.is_custom_script || (detailTask.vertical && detailTask.avatar && detailTask.pain_point && detailTask.style);
            const hasScript = isConceptCell || detailTask.is_custom_script || !!detailTask.script_text;
            const status = detailTask.status;
            let stage;
            if (!fieldsReady) stage = 'setup';
            else if (!hasScript) stage = 'generate';
            else if (status === 'draft') stage = 'ready';
            else if (status === 'assigned' || status === 'in_progress' || status === 'needs_revision') stage = 'produce';
            else if (status === 'submitted') stage = 'review';
            else if (status === 'approved') stage = 'launch';
            else stage = 'done';
            const show = (key) => showEverythingInDetail || ({
              fields: ['setup'].includes(stage),
              script: ['generate', 'ready', 'produce', 'review', 'launch'].includes(stage),
              deliverables: ['produce', 'review', 'launch', 'done'].includes(stage),
              history: false,
              comments: false,
            }[key] ?? true);
            // Opens the Launch-to-Facebook modal and kicks off structure/prefill/
            // copy loading. Shared by the "Next Action" CTA and the action-row
            // button so the two can't drift — a missing setFbLaunchOpen(true) on
            // the CTA is exactly what silently made it a no-op.
            const openFbLaunch = async () => {
              setFbLaunchTask(detailTask);
              setFbLaunchResult(null);
              setFbHeadlineOptions([]);
              // Pre-fill defaults
              setFbLaunchForm((prev) => ({
                ...prev,
                ad_account_id: '', campaign_id: '', ad_set_ids: [],
                headline: detailTask.hook_text || '',
                primary_text: detailTask.script_text || '',
                description: '',
                cta_type: 'LEARN_MORE',
                destination_url: '',
                display_link: '',
                page_id: '',
                page_name: '',
              }));
              setFbLaunchOpen(true);
              setFbShowInactive(false);
              // Load DB structure (instant) + prefill + auto-generate ad copy in parallel
              if (detailTask.client) {
                setFbStructureLoading(true);
                setFbCopyGenerating(true);
                setFbHeadlineOptions([]);
                try {
                  const [structRes, prefillRes, copyRes] = await Promise.all([
                    fetchFacebookAdStructureDB(detailTask.client),
                    fetchLaunchPrefill(detailTask.id),
                    generateAdCopy(detailTask.id).catch(() => null),
                  ]);
                  setFbStructure(structRes?.data || []);
                  const pf = prefillRes?.data || {};
                  const copy = copyRes?.data || {};
                  const headlines = copy.headlines || (copy.headline ? [copy.headline] : []);
                  if (headlines.length) setFbHeadlineOptions(headlines);
                  setFbLaunchForm((prev) => ({
                    ...prev,
                    destination_url: pf.destination_url || prev.destination_url,
                    display_link: pf.display_link || prev.display_link,
                    page_id: pf.page_id || prev.page_id,
                    page_name: pf.page_name || '',
                    headline: headlines[0] || prev.headline,
                    primary_text: copy.primary_text || prev.primary_text,
                    description: copy.description || prev.description,
                  }));
                } catch (_e) { setFbStructure([]); }
                setFbStructureLoading(false);
                setFbCopyGenerating(false);
              }
            };
            // Determine the single primary "Next Action" CTA based on stage.
            const primaryAction = (() => {
              if (stage === 'setup') return { label: 'Fill in vertical, avatar, pain point, and style to continue', disabled: true, icon: null, onClick: null };
              if (stage === 'generate' && detailTask.is_custom_script) return { label: 'Paste a custom script below to continue', disabled: true, icon: null, onClick: null };
              if (stage === 'generate') return { label: variantsGenerating ? 'Generating…' : (variantsRanking ? 'Ranking…' : 'Auto-generate variants'), disabled: variantsGenerating || variantsRanking, icon: <AutoFixHighIcon />, onClick: handleGenerateVariants };
              if (stage === 'ready' && isStaff && detailTask.is_complete) return { label: 'Assign to Designer', disabled: Boolean(actionLoading), icon: <PersonAddIcon />, onClick: () => handleAction(assignCreativeTask, detailTask.id, 'assign') };
              if (stage === 'produce' && ['assigned', 'needs_revision'].includes(status)) return { label: 'Start Work', disabled: Boolean(actionLoading), icon: <PlayArrowIcon />, onClick: () => handleAction(startCreativeTask, detailTask.id, 'start') };
              if (stage === 'produce' && ['in_progress'].includes(status)) return { label: 'Submit for Review', disabled: Boolean(actionLoading), icon: <SendIcon />, onClick: () => setSubmitOpen(true) };
              if (stage === 'review' && isStaff) return { label: 'Approve', disabled: Boolean(actionLoading), icon: <CheckCircleIcon />, color: 'success', onClick: async () => {
                setActionLoading('approve');
                try {
                  await approveCreativeTask(detailTask.id, approveRating ? { rating: approveRating } : {});
                  const res = await fetchCreativeTask(detailTask.id);
                  setDetailTask(res.data);
                  const flightId = detailTask.flight;
                  if (flightId && flightTasks[flightId]) {
                    setFlightTasks((prev) => ({ ...prev, [flightId]: prev[flightId].map((t) => t.id === detailTask.id ? { ...t, ...res.data } : t) }));
                  }
                  loadDesignerBanks();
                } catch (err) { setError(getApiErrorMessage(err, 'Approve failed')); }
                setActionLoading('');
              } };
              if (stage === 'launch' && isStaff) return { label: 'Launch to Facebook', disabled: Boolean(actionLoading), icon: null, color: 'info', onClick: openFbLaunch };
              return null;
            })();
            // Status pipeline strip — shows the linear lifecycle so "what's next" is visible.
            const PIPELINE = [
              { key: 'draft', label: 'Draft' },
              { key: 'assigned', label: 'Assigned' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'submitted', label: 'Submitted' },
              { key: 'approved', label: 'Approved' },
              { key: 'complete', label: 'Complete' },
            ];
            const pipelineIdx = Math.max(0, PIPELINE.findIndex((p) => p.key === status));
            return (
            <Stack spacing={2.5}>
              {/* Structured name + status header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Structured Name</Typography>
                    {detailTask.custom_ad_name ? (
                      <Chip label="overridden" size="small" variant="outlined" sx={{ height: 14, fontSize: '0.6rem' }} />
                    ) : null}
                  </Stack>
                  {adNameEditing ? (
                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      value={adNameDraft}
                      onChange={(e) => setAdNameDraft(e.target.value)}
                      onBlur={handleSaveAdName}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                        if (e.key === 'Escape') { setAdNameEditing(false); setAdNameDraft(''); }
                      }}
                      disabled={adNameSaving}
                      placeholder={detailTask.structured_name}
                      helperText={detailTask.custom_ad_name ? 'Clear and press Enter to revert to the auto-generated name.' : 'Press Enter to save. Escape to cancel.'}
                      InputProps={{ sx: { fontFamily: 'monospace', fontWeight: 700 } }}
                    />
                  ) : (
                    <Tooltip title="Click to edit. Leave blank to revert to auto-generated name.">
                      <Typography
                        variant="body1"
                        onClick={() => { setAdNameDraft(detailTask.custom_ad_name || detailTask.structured_name || ''); setAdNameEditing(true); }}
                        sx={{
                          fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all',
                          cursor: 'pointer',
                          borderRadius: 0.5, px: 0.5, mx: -0.5,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        {detailTask.structured_name}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
                <Chip label={detailTask.status_display} sx={STATUS_CHIP_SX[detailTask.status] || STATUS_CHIP_SX.draft} />
              </Stack>

              {/* Status pipeline strip — linear lifecycle so "what's next?" is visible at a glance */}
              <Stack direction="row" spacing={0} alignItems="center" sx={{ overflowX: 'auto' }}>
                {PIPELINE.map((p, i) => {
                  const isCurrent = i === pipelineIdx;
                  const isPast = i < pipelineIdx;
                  return (
                    <React.Fragment key={p.key}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Box sx={{
                          width: 10, height: 10, borderRadius: '50%',
                          bgcolor: isCurrent ? 'primary.main' : (isPast ? 'success.main' : 'grey.300'),
                          border: isCurrent ? '2px solid' : 'none',
                          borderColor: 'primary.dark',
                        }} />
                        <Typography variant="caption" sx={{ fontWeight: isCurrent ? 700 : 400, color: isCurrent ? 'primary.main' : (isPast ? 'success.main' : 'text.secondary') }}>
                          {p.label}
                        </Typography>
                      </Stack>
                      {i < PIPELINE.length - 1 ? (
                        <Box sx={{ height: 2, flexGrow: 1, minWidth: 16, mx: 1, bgcolor: i < pipelineIdx ? 'success.main' : 'grey.300' }} />
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </Stack>

              {/* Primary "Next Action" CTA — one obvious move based on stage */}
              {primaryAction ? (
                <Card variant="outlined" sx={{ p: 2, borderColor: primaryAction.disabled ? 'grey.300' : 'primary.main', borderWidth: 2, bgcolor: primaryAction.disabled ? 'background.default' : 'rgba(25, 118, 210, 0.04)' }}>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>Next Action</Typography>
                      <Typography variant="body2">{primaryAction.label}</Typography>
                    </Box>
                    {primaryAction.onClick ? (
                      <Button
                        variant="contained" color={primaryAction.color || 'primary'} size="medium"
                        startIcon={primaryAction.icon}
                        disabled={primaryAction.disabled}
                        onClick={primaryAction.onClick}
                      >
                        {primaryAction.label}
                      </Button>
                    ) : null}
                  </Stack>
                </Card>
              ) : null}

              {/* ===== CLIENT CONTEXT ===== */}
              {!isConceptCell && detailTask.client_context ? (() => {
                const ctx = detailTask.client_context;
                const hasContext = ctx.notes || ctx.website_url || ctx.logo_url || (ctx.offers && ctx.offers.length) || ctx.client_assets || ctx.shared_assets || ctx.bath_manufacturer;
                if (!hasContext) return null;
                return (
                  <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2" color="primary">Client Context — {detailTask.client_name}</Typography>

                        {/* Quick links row */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {ctx.website_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.website_url} target="_blank" rel="noopener noreferrer">
                              Website
                            </Button>
                          ) : null}
                          {ctx.logo_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.logo_url} target="_blank" rel="noopener noreferrer">
                              Logo
                            </Button>
                          ) : null}
                          {ctx.client_assets?.client_asset_folder_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.client_assets.client_asset_folder_url} target="_blank" rel="noopener noreferrer">
                              Client Assets
                            </Button>
                          ) : null}
                          {ctx.shared_assets?.general_asset_folder_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.shared_assets.general_asset_folder_url} target="_blank" rel="noopener noreferrer">
                              Shared Assets
                            </Button>
                          ) : null}
                          {ctx.shared_assets?.recent_ad_examples_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.shared_assets.recent_ad_examples_url} target="_blank" rel="noopener noreferrer">
                              Recent Ads
                            </Button>
                          ) : null}
                          {detailTask.frame_io_url ? (
                            <Button size="small" variant="contained" color="info" startIcon={<OpenInNewIcon />} component="a" href={detailTask.frame_io_url} target="_blank" rel="noopener noreferrer">
                              Frame.io
                            </Button>
                          ) : null}
                        </Stack>

                        {/* Offers */}
                        {ctx.offers && ctx.offers.length ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Offers for {detailTask.vertical_name}</Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              {ctx.offers.map((o) => (
                                <Chip
                                  key={o.id}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  label={`${o.offer_type_display}: ${o.display_label}${o.financing_details ? ` (${o.financing_details})` : ''}`}
                                />
                              ))}
                            </Stack>
                          </Box>
                        ) : null}

                        {/* Bath manufacturer */}
                        {ctx.bath_manufacturer ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Bath Manufacturer</Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }}>{ctx.bath_manufacturer}</Typography>
                          </Box>
                        ) : null}

                        {/* Brand notes */}
                        {ctx.notes ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Brand Notes</Typography>
                            <Typography variant="body2" sx={{ mt: 0.25, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {ctx.notes}
                            </Typography>
                          </Box>
                        ) : null}

                        {/* AI instructions — hidden from designer view, used internally by AI */}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })() : null}

              {/* Task parameters — always show for context, but tighter when not in setup stage */}
              {show('fields') ? (
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Client</Typography><Typography variant="body2" fontWeight={600}>{detailTask.client_name}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Vertical</Typography><Typography variant="body2">{detailTask.vertical_name || '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Designer</Typography><Typography variant="body2">{detailTask.designer_name || '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Avatar</Typography><Typography variant="body2">{detailTask.avatar_name || '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Pain Point</Typography><Typography variant="body2">{detailTask.pain_point_label || '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Style</Typography><Typography variant="body2">{detailTask.style_label || '—'}</Typography></Grid>
                  <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Audience</Typography><Typography variant="body2">{AUDIENCE_OPTIONS.find((o) => o.value === detailTask.audience)?.label || 'All Ages'}</Typography></Grid>
                </Grid>
              ) : null}

              {/* Hook — show in everything mode (compact one-liner outside of full mode is handled inside script section) */}
              {show('fields') && detailTask.hook_text ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={9}><Typography variant="caption" color="text.secondary">Hook</Typography><Typography variant="body2">{detailTask.hook_text}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption" color="text.secondary">Hook Slug</Typography><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{detailTask.hook_slug || '—'}</Typography></Grid>
                </Grid>
              ) : null}

              {/* Task Notes — surfaced prominently so designers don't miss direction from the assigner.
                  Hidden for Concept × Cell tasks: notes are auto-boilerplate there, and the
                  lane-master banner + live spec already convey the spec/offer. */}
              {!isConceptCell && detailTask.notes ? (
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Task Notes for Designer</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{detailTask.notes}</Typography>
                </Alert>
              ) : null}

              {/* Concept × Cell: this ONE task is the LANE MASTER — make it
                  unmistakable that the single deliverable serves every client
                  the brief was deployed to (not a single-client task). */}
              {isConceptCell && detailTask.lane_brief ? (
                <Alert severity="success" icon={<InfoOutlinedIcon />} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Per-client task — part of a lane master
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {detailTask.lane_brief.lane_name
                      ? <><strong>{detailTask.lane_brief.lane_name}</strong> · </>
                      : null}
                    This is the <strong>{detailTask.client_name}</strong> deliverable
                    {' '}— one of <strong>{detailTask.lane_brief.client_count || 0}</strong> client
                    tasks off the same master spec. Produce this client's video to the spec
                    below (their offer is highlighted in it). The sibling client tasks:
                  </Typography>
                  {(detailTask.lane_brief.clients || []).length > 0 ? (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {detailTask.lane_brief.clients.map((c, i) => (
                        <Chip
                          key={`${c.client_name}-${i}`}
                          size="small"
                          variant="outlined"
                          label={`${c.client_name}${c.offer ? ` · ${c.offer}` : ''} — ${c.task_status_display || c.status_display || c.status}`}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Not deployed to any clients yet — deploy the master from the Creative Engine briefs page.
                    </Typography>
                  )}
                </Alert>
              ) : null}

              {/* Concept × Cell: the linked brief markdown IS the full spec —
                  show it in its entirety, always, right in the task. */}
              {isConceptCell && detailTask.lane_brief?.rendered_brief ? (
                <Card variant="outlined" sx={{ p: 0 }}>
                  <Box sx={{ px: 2, py: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Designer Brief — full spec
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {detailTask.vertical_name ? `${detailTask.vertical_name} · ` : ''}
                      {detailTask.lane_brief.concept_name || ''}
                      {` · ${detailTask.lane_brief.brief_code}`}
                    </Typography>
                    {detailTask.lane_brief.cell_name ? (
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        Style: {detailTask.lane_brief.cell_name} — creative recipe, not the ad's subject
                      </Typography>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      p: 2.5, maxHeight: 540, overflow: 'auto',
                      '& h1': { fontSize: '1.3rem', fontWeight: 700, mt: 0, mb: 1 },
                      '& h2': { fontSize: '1.02rem', fontWeight: 700, mt: 2.5, mb: 1 },
                      '& p': { my: 0.75, lineHeight: 1.5 },
                      '& ul': { my: 0.5, pl: 3 }, '& li': { my: 0.25 },
                      '& table': { borderCollapse: 'collapse', width: '100%', my: 1, fontSize: '0.85rem' },
                      '& th, & td': { border: '1px solid', borderColor: 'divider', p: 0.75, textAlign: 'left', verticalAlign: 'top' },
                      '& th': { bgcolor: 'grey.100', fontWeight: 700 },
                      '& code': { bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontSize: '0.85em' },
                      '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', my: 2 },
                    }}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children }) => {
                          const dyn = typeof href === 'string' && href.startsWith('https://dyn.lgp/');
                          if (!dyn) {
                            return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                          }
                          const slot = href.replace('https://dyn.lgp/', '');
                          return (
                            <Tooltip title={`Auto-filled per client (${slot})`} arrow>
                              <Box
                                component="span"
                                sx={{
                                  bgcolor: '#fde68a',
                                  color: '#713f12',
                                  fontWeight: 700,
                                  px: 0.5,
                                  borderRadius: 0.5,
                                  whiteSpace: 'nowrap',
                                  boxShadow: 'inset 0 -2px 0 #f59e0b',
                                }}
                              >
                                {children}
                              </Box>
                            </Tooltip>
                          );
                        },
                      }}
                    >
                      {detailTask.lane_brief.rendered_brief}
                    </ReactMarkdown>
                  </Box>
                </Card>
              ) : null}

              {/* Style preview — what this style actually looks/sounds like */}
              {(detailTask.style_label && (detailTask.style_description || detailTask.style_reference_url)) ? (
                <Card variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Box sx={{ minWidth: 140 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>Creative Style</Typography>
                      <Typography variant="body2" fontWeight={700}>{detailTask.style_label}</Typography>
                    </Box>
                    {detailTask.style_description ? (
                      <Typography variant="body2" sx={{ flexGrow: 1, whiteSpace: 'pre-wrap' }}>{detailTask.style_description}</Typography>
                    ) : <Box sx={{ flexGrow: 1 }} />}
                    {detailTask.style_reference_url ? (
                      <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={detailTask.style_reference_url} target="_blank" rel="noopener noreferrer">
                        See Example
                      </Button>
                    ) : null}
                  </Stack>
                </Card>
              ) : null}

              {/* Script — gated by progressive disclosure. Hidden for
                  Concept × Cell tasks: they have no generated script/variants;
                  the brief markdown above is the spec. */}
              {show('script') && !isConceptCell ? (<><Divider />
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2">Ad Script</Typography>
                {currentScript ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title={currentScript.thumb === 1 ? 'Remove thumbs-up' : 'Thumbs up — feeds future generations as a positive example'}>
                      <IconButton size="small" disabled={thumbingScriptId === currentScript.id} onClick={() => handleThumbScript(currentScript.id, currentScript.thumb === 1 ? 0 : 1)} color={currentScript.thumb === 1 ? 'success' : 'default'}>
                        {currentScript.thumb === 1 ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={currentScript.thumb === -1 ? 'Remove thumbs-down' : 'Thumbs down — feeds future generations as a negative example'}>
                      <IconButton size="small" disabled={thumbingScriptId === currentScript.id} onClick={() => handleThumbScript(currentScript.id, currentScript.thumb === -1 ? 0 : -1)} color={currentScript.thumb === -1 ? 'error' : 'default'}>
                        {currentScript.thumb === -1 ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : null}
                <Box sx={{ flexGrow: 1 }} />
                {scriptHistory.length > 0 ? (
                  <Button size="small" startIcon={<HistoryIcon />} onClick={() => setScriptHistoryOpen((v) => !v)}>
                    {scriptHistoryOpen ? 'Hide History' : `History (${scriptHistory.length})`}
                  </Button>
                ) : null}
              </Stack>
              {detailTask.is_custom_script ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="caption" color="text.secondary">
                      Custom-script mode — paste your script below. Avatar / pain point / hook / style are optional in this mode.
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      placeholder="Paste the ad script for this task…"
                      value={customScriptDraft}
                      onChange={(e) => setCustomScriptDraft(e.target.value)}
                      disabled={customScriptSaving}
                      InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveCustomScript}
                        disabled={customScriptSaving || customScriptDraft === (detailTask.script_text || '')}
                      >
                        {customScriptSaving ? 'Saving…' : 'Save Script'}
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              ) : null}
              {!detailTask.is_custom_script && !(detailTask.vertical && detailTask.avatar && detailTask.pain_point && detailTask.style) ? (
                <Typography variant="body2" color="text.secondary">Fill in vertical, avatar, pain point, and style first to generate variants.</Typography>
              ) : null}

              {/* Compare hook variants — primary script generation flow. AI picks 3 hooks by default, generates N variants each, ranks them. */}
              {isStaff && !detailTask.is_custom_script && detailTask.vertical && detailTask.avatar && detailTask.pain_point && detailTask.style ? (
                <Accordion expanded={variantsPanelOpen} onChange={(_e, v) => setVariantsPanelOpen(v)} sx={{ '&:before': { display: 'none' } }} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2">Compare hook variants</Typography>
                      <Typography variant="caption" color="text.secondary">
                        AI picks 3 hooks, generates {variantCount} scripts each, then ranks them.
                      </Typography>
                      {variantsRunIds.length > 0 ? (
                        <Chip size="small" color="primary" variant="outlined" label={`${variantsRunIds.length} from last run`} sx={{ height: 18 }} />
                      ) : null}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                            Hooks {variantHookSlugs.length ? `(${variantHookSlugs.length} picked, max 5)` : '— AI will pick 3'}
                          </Typography>
                          <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={() => setVariantHooksCustomOpen((v) => !v)}>
                            {variantHooksCustomOpen ? 'Hide' : 'Customize'}
                          </Button>
                          {variantHookSlugs.length > 0 ? (
                            <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={() => setVariantHookSlugs([])}>
                              Clear (let AI pick)
                            </Button>
                          ) : null}
                        </Stack>
                        {variantHooksCustomOpen ? (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {hookTypes.filter((h) => h.active !== false).map((h) => {
                              const selected = variantHookSlugs.includes(h.slug);
                              return (
                                <Chip
                                  key={h.slug}
                                  label={h.label}
                                  size="small"
                                  color={selected ? 'primary' : 'default'}
                                  variant={selected ? 'filled' : 'outlined'}
                                  onClick={() => toggleVariantHookSlug(h.slug)}
                                  disabled={variantsGenerating || (!selected && variantHookSlugs.length >= 5)}
                                />
                              );
                            })}
                          </Stack>
                        ) : null}
                      </Box>

                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <TextField
                          size="small"
                          select
                          label="Variants per hook"
                          value={variantCount}
                          onChange={(e) => setVariantCount(Number(e.target.value))}
                          sx={{ width: 160 }}
                          disabled={variantsGenerating}
                        >
                          {[1, 2, 3, 4, 5].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </TextField>
                        <Typography variant="caption" color="text.secondary">
                          {(variantHookSlugs.length || 3) * variantCount} scripts → auto-ranked. {variantHookSlugs.length === 0 ? 'AI will pick 3 hooks.' : ''}
                        </Typography>
                      </Stack>

                      <TextField
                        size="small" fullWidth multiline minRows={2}
                        label="Steering Prompt (optional)"
                        value={steeringPromptDraft}
                        onChange={(e) => setSteeringPromptDraft(e.target.value)}
                        placeholder={detailTask.default_steering_prompt ? `Defaults to: "${detailTask.default_steering_prompt}". Add one-off direction here, e.g. "lean harder into urgency"` : 'Optional direction, e.g. "lean harder into urgency about active leaks"'}
                        helperText={detailTask.default_steering_prompt ? 'Merged with the task default steering prompt.' : 'Used for this run only. Set a task-level default in the Edit dialog.'}
                      />

                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          size="small" variant="contained" startIcon={<AutoFixHighIcon />}
                          disabled={variantsGenerating || variantsRanking}
                          onClick={handleGenerateVariants}
                        >
                          {variantsGenerating ? 'Generating…' : (variantsRanking ? 'Ranking…' : (variantHookSlugs.length === 0 ? 'Auto-generate variants' : 'Generate variants'))}
                        </Button>
                        {variantsRunIds.length >= 2 ? (
                          <Tooltip title="Re-run the judge pass against the current variants">
                            <Button
                              size="small" variant="outlined" startIcon={<AutoFixHighIcon />}
                              disabled={variantsGenerating || variantsRanking}
                              onClick={handleRankVariants}
                            >
                              {variantsRanking ? 'Ranking…' : 'Re-rank'}
                            </Button>
                          </Tooltip>
                        ) : null}
                      </Stack>

                      {(variantsGenerating || variantsRanking) ? <LinearProgress /> : null}

                      {variantsErrors.length > 0 ? (
                        <Alert severity="warning">
                          {variantsErrors.length} variant{variantsErrors.length === 1 ? '' : 's'} failed: {variantsErrors.map((e) => `${e.hook_slug} #${e.variant_index + 1}`).join(', ')}
                        </Alert>
                      ) : null}

                      {variantsAutoPickedHooks.length > 0 ? (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          AI picked these hooks for this task:&nbsp;
                          <strong>{variantsAutoPickedHooks.map((s) => hookTypes.find((h) => h.slug === s)?.label || s).join(' · ')}</strong>
                        </Alert>
                      ) : null}

                      {variantsRunIds.length > 0 ? (() => {
                        const runScripts = scriptHistory.filter((s) => variantsRunIds.includes(s.id));
                        const hookLabel = (slug) => hookTypes.find((h) => h.slug === slug)?.label || slug;
                        const anyRanked = runScripts.some((s) => s.ai_rank != null);
                        const rankMedal = (r) => (r === 1 ? '🏆' : r === 2 ? '🥈' : r === 3 ? '🥉' : '');
                        const rankLabel = (r) => {
                          const medal = rankMedal(r);
                          return medal ? `${medal} #${r}` : `#${r}`;
                        };
                        const top3Scripts = variantsTop3
                          .map((id) => runScripts.find((s) => s.id === id))
                          .filter(Boolean);
                        const byHook = {};
                        runScripts.forEach((s) => {
                          const key = s.hook_slug || 'unknown';
                          (byHook[key] ||= []).push(s);
                        });
                        Object.keys(byHook).forEach((k) => {
                          byHook[k].sort((a, b) => {
                            const ar = a.ai_rank == null ? 9999 : a.ai_rank;
                            const br = b.ai_rank == null ? 9999 : b.ai_rank;
                            return ar - br;
                          });
                        });
                        const orderedHookSlugs = Object.keys(byHook).sort((a, b) => {
                          const bestA = byHook[a][0]?.ai_rank == null ? 9999 : byHook[a][0].ai_rank;
                          const bestB = byHook[b][0]?.ai_rank == null ? 9999 : byHook[b][0].ai_rank;
                          return bestA - bestB;
                        });
                        return (
                          <Stack spacing={2}>
                            {top3Scripts.length > 0 ? (
                              <Card variant="outlined" sx={{ p: 1.5, borderColor: 'success.main', bgcolor: 'success.lighter', '&.MuiPaper-root': { bgcolor: 'rgba(76,175,80,0.08)' } }}>
                                <Typography variant="caption" color="success.main" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                                  AI Top 3
                                </Typography>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  {top3Scripts.map((s, i) => (
                                    <Stack key={s.id} direction="row" spacing={1} alignItems="flex-start">
                                      <Chip size="small" label={rankLabel(i + 1)} color="success" sx={{ height: 22, fontWeight: 700, fontSize: '0.85rem' }} />
                                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', minWidth: 50 }}>
                                        #{s.id}
                                      </Typography>
                                      <Typography variant="caption" sx={{ flexGrow: 1 }}>
                                        <strong>{hookLabel(s.hook_slug)}</strong>
                                        {s.ai_rank_reason ? ` — ${s.ai_rank_reason}` : ''}
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Stack>
                              </Card>
                            ) : null}
                            {orderedHookSlugs.map((slug) => (
                              <Box key={slug}>
                                <Typography variant="caption" color="primary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                                  {hookLabel(slug)} · {byHook[slug].length} variant{byHook[slug].length === 1 ? '' : 's'}
                                </Typography>
                                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                                  {byHook[slug].map((s) => {
                                    const isTop3 = variantsTop3.includes(s.id);
                                    const borderColor = s.is_selected ? 'primary.main' : (isTop3 ? 'success.main' : undefined);
                                    return (
                                      <Grid item xs={12} md={6} key={s.id}>
                                        <Card variant="outlined" sx={{ p: 1.5, height: '100%', borderColor, borderWidth: borderColor ? 2 : 1 }}>
                                          <Stack spacing={1} sx={{ height: '100%' }}>
                                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                              {s.ai_rank != null ? (
                                                <Chip
                                                  size="small"
                                                  label={rankLabel(s.ai_rank)}
                                                  color={s.ai_rank <= 3 ? 'success' : 'default'}
                                                  variant={s.ai_rank <= 3 ? 'filled' : 'outlined'}
                                                  sx={{ height: 22, fontWeight: 700, fontSize: s.ai_rank <= 3 ? '0.85rem' : '0.75rem' }}
                                                />
                                              ) : (anyRanked ? <Chip size="small" label="unranked" sx={{ height: 18 }} /> : null)}
                                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>#{s.id}</Typography>
                                              {s.is_selected ? <Chip size="small" label="Active" color="primary" sx={{ height: 18 }} /> : null}
                                              <Box sx={{ flexGrow: 1 }} />
                                              <IconButton size="small" disabled={thumbingScriptId === s.id} onClick={() => handleThumbScript(s.id, s.thumb === 1 ? 0 : 1)} color={s.thumb === 1 ? 'success' : 'default'}>
                                                {s.thumb === 1 ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                                              </IconButton>
                                              <IconButton size="small" disabled={thumbingScriptId === s.id} onClick={() => handleThumbScript(s.id, s.thumb === -1 ? 0 : -1)} color={s.thumb === -1 ? 'error' : 'default'}>
                                                {s.thumb === -1 ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
                                              </IconButton>
                                              {!s.is_selected ? (
                                                <Button size="small" variant="outlined" onClick={() => handleSelectScript(s.id)}>Make Active</Button>
                                              ) : null}
                                            </Stack>
                                            {s.ai_rank_reason ? (
                                              <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: s.ai_rank != null && s.ai_rank <= 3 ? 'success.main' : 'grey.400' }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                  Judge: {s.ai_rank_reason}
                                                </Typography>
                                              </Box>
                                            ) : null}
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.78rem', flexGrow: 1 }}>
                                              {s.script_text}
                                            </Typography>
                                          </Stack>
                                        </Card>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Box>
                            ))}
                          </Stack>
                        );
                      })() : null}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ) : null}

              {detailTask.script_text || activeScriptEditing ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>Active Script</Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {activeScriptEditing ? (
                      <>
                        <Button size="small" onClick={handleCancelActiveScript} disabled={activeScriptSaving} startIcon={<UndoIcon />}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={handleSaveActiveScript} disabled={activeScriptSaving} startIcon={<SaveIcon />}>
                          {activeScriptSaving ? 'Saving…' : 'Save'}
                        </Button>
                      </>
                    ) : (
                      <Button size="small" variant="outlined" onClick={handleEditActiveScript} startIcon={<EditIcon />}>Edit</Button>
                    )}
                  </Stack>
                  {activeScriptEditing ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        minRows={8}
                        value={activeScriptDraft}
                        onChange={(e) => setActiveScriptDraft(e.target.value)}
                        disabled={activeScriptSaving}
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Edits save to this task only — the variant cards above stay as the AI generated them. Clicking &quot;Make Active&quot; on a variant will overwrite these edits.
                      </Typography>
                    </>
                  ) : (
                    <Stack spacing={1.5}>
                      {detailTask.script_text.split(/\n\n+/).map((block, i) => {
                        const trimmed = block.trim();
                        if (!trimmed) return null;
                        const labelMatch = trimmed.match(/^(HOOK_SLUG|HOOK|BODY|OFFER|CTA|SCRIPT)[^:]*:\s*(.*)/s);
                        if (labelMatch) {
                          return (
                            <Box key={i}>
                              <Typography variant="caption" color="primary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>{labelMatch[1]}</Typography>
                              <Typography variant="body2" sx={{ mt: 0.25 }}>{labelMatch[2].trim()}</Typography>
                            </Box>
                          );
                        }
                        return <Typography key={i} variant="body2">{trimmed}</Typography>;
                      })}
                    </Stack>
                  )}
                </Card>
              ) : null}

              {/* Script history — earlier generations with their steering + thumbs */}
              {scriptHistoryOpen && scriptHistory.length > 0 ? (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>Script History</Typography>
                  {scriptHistory.map((s) => (
                    <Card key={s.id} variant="outlined" sx={{ p: 1.5, opacity: s.is_selected ? 1 : 0.85 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            #{s.id} · {s.created_at ? new Date(s.created_at).toLocaleString() : ''}
                          </Typography>
                          {s.is_selected ? <Chip size="small" label="Active" color="primary" sx={{ height: 18 }} /> : null}
                          {s.parent_script ? <Chip size="small" label={`from #${s.parent_script}`} variant="outlined" sx={{ height: 18 }} /> : null}
                          {s.created_by_name ? <Typography variant="caption" color="text.secondary">by {s.created_by_name}</Typography> : null}
                          <Box sx={{ flexGrow: 1 }} />
                          <IconButton size="small" disabled={thumbingScriptId === s.id} onClick={() => handleThumbScript(s.id, s.thumb === 1 ? 0 : 1)} color={s.thumb === 1 ? 'success' : 'default'}>
                            {s.thumb === 1 ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" disabled={thumbingScriptId === s.id} onClick={() => handleThumbScript(s.id, s.thumb === -1 ? 0 : -1)} color={s.thumb === -1 ? 'error' : 'default'}>
                            {s.thumb === -1 ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
                          </IconButton>
                          {!s.is_selected ? (
                            <Button size="small" onClick={() => handleSelectScript(s.id)}>Make Active</Button>
                          ) : null}
                        </Stack>
                        {s.steering_prompt ? (
                          <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'primary.light' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Steering used</Typography>
                            <Typography variant="caption" component="div" sx={{ whiteSpace: 'pre-wrap' }}>{s.steering_prompt}</Typography>
                          </Box>
                        ) : null}
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.78rem' }}>{s.script_text}</Typography>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              ) : null}</>) : null}

              {/* Deliverables — Frame.io review link + uploaded files, grouped since both are post-production handoff artifacts */}
              {show('deliverables') ? (<><Divider />
              <Typography variant="subtitle2">Deliverables</Typography>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <TextField
                    size="small" fullWidth
                    label="Frame.io Link"
                    type="url"
                    value={detailTask.frame_io_url || ''}
                    placeholder="frame.io/… or https://app.frame.io/…"
                    error={!!frameIoError}
                    helperText={frameIoError || ' '}
                    onChange={(e) => {
                      setDetailTask((prev) => ({ ...prev, frame_io_url: e.target.value }));
                      if (frameIoError) setFrameIoError('');
                    }}
                    onBlur={async (e) => {
                      const raw = e.target.value.trim();
                      // Auto-prefix scheme so designers can paste bare hosts.
                      const url = raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw;
                      if (url === (detailTask._saved_frame_io_url || '')) {
                        setFrameIoError('');
                        return;
                      }
                      if (url) {
                        try { new URL(url); }
                        catch { setFrameIoError('Not a valid URL.'); return; }
                      }
                      try {
                        await updateCreativeTask(detailTask.id, { frame_io_url: url });
                        setDetailTask((prev) => ({
                          ...prev, frame_io_url: url, _saved_frame_io_url: url,
                        }));
                        setFrameIoError('');
                      } catch (err) {
                        const msg = err?.response?.data?.frame_io_url
                          || err?.response?.data?.detail
                          || getApiErrorMessage(err, 'Not a valid URL.');
                        setFrameIoError(Array.isArray(msg) ? msg[0] : String(msg));
                      }
                    }}
                    InputProps={{
                      endAdornment: detailTask.frame_io_url ? (
                        <IconButton size="small" component="a" href={detailTask.frame_io_url} target="_blank" rel="noopener noreferrer" title="Open in Frame.io">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      ) : null,
                    }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                      Files ({(detailTask.files || []).length})
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} size="small" disabled={Boolean(actionLoading)}>
                      {actionLoading === 'upload' ? 'Uploading...' : 'Upload File'}<input type="file" hidden onChange={handleFileUpload} />
                    </Button>
                  </Stack>
                  {(detailTask.files || []).length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No files uploaded yet.</Typography>
                  ) : (detailTask.files || []).map((file) => (
                    <Stack key={file.id} direction="row" spacing={1} alignItems="center">
                      <Chip label={file.file_type} size="small" variant="outlined" />
                      <Typography variant="body2" component="a" href={file.file} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'underline' }}>{file.original_name || 'Download'}</Typography>
                      <Typography variant="caption" color="text.secondary">by {file.uploaded_by_name} &middot; {new Date(file.created_at).toLocaleString()}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Card></>) : null}

              {/* Workflow actions — only the full row when "Show everything" is on, since the Next Action banner up top handles the primary CTA in focused mode */}
              {showEverythingInDetail ? (<><Divider />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {isStaff ? <Button variant="outlined" startIcon={<EditIcon />} size="small" onClick={() => openEditTask(detailTask)}>Edit</Button> : null}
                {isStaff && detailTask.status === 'draft' && detailTask.is_complete ? (
                  <Button variant="contained" color="primary" startIcon={<PersonAddIcon />} size="small" disabled={Boolean(actionLoading)} onClick={() => handleAction(assignCreativeTask, detailTask.id, 'assign')}>
                    Assign to Designer
                  </Button>
                ) : null}
                {isStaff && detailTask.status === 'draft' && !detailTask.is_complete ? (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Fill in all fields and generate a script before assigning.</Typography>
                ) : null}
                {['assigned', 'needs_revision'].includes(detailTask.status) ? <Button variant="contained" startIcon={<PlayArrowIcon />} size="small" disabled={Boolean(actionLoading)} onClick={() => handleAction(startCreativeTask, detailTask.id, 'start')}>Start Work</Button> : null}
                {['in_progress', 'needs_revision', 'assigned'].includes(detailTask.status) ? <Button variant="contained" startIcon={<SendIcon />} size="small" disabled={Boolean(actionLoading)} onClick={() => setSubmitOpen(true)}>Submit for Review</Button> : null}
                {isStaff && ['submitted', 'needs_revision', 'in_progress'].includes(detailTask.status) ? (
                  <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} size="small" disabled={Boolean(actionLoading)} onClick={async () => {
                    setActionLoading('approve');
                    try {
                      await approveCreativeTask(detailTask.id, approveRating ? { rating: approveRating } : {});
                      const res = await fetchCreativeTask(detailTask.id);
                      setDetailTask(res.data);
                      const flightId = detailTask.flight;
                      if (flightId && flightTasks[flightId]) {
                        setFlightTasks((prev) => ({ ...prev, [flightId]: prev[flightId].map((t) => t.id === detailTask.id ? { ...t, ...res.data } : t) }));
                      }
                      loadDesignerBanks();
                    } catch (err) { setError(getApiErrorMessage(err, 'Approve failed')); }
                    setActionLoading('');
                  }}>Approve</Button>
                ) : null}
                {isStaff && ['submitted', 'approved', 'in_progress'].includes(detailTask.status) ? (
                  <Button variant="outlined" color="warning" startIcon={<UndoIcon />} size="small" disabled={Boolean(actionLoading)} onClick={() => setRevisionOpen(true)}>Request Revision</Button>
                ) : null}
                {isStaff && detailTask.status === 'approved' ? (
                  <Button variant="contained" color="info" size="small" disabled={Boolean(actionLoading)} onClick={openFbLaunch}>
                    {detailTask.facebook_ad_id ? 'Relaunch to Facebook' : 'Launch to Facebook'}
                  </Button>
                ) : null}
                {detailTask.facebook_ad_id ? (
                  <Chip label={`FB Ad: ${detailTask.facebook_ad_id}`} size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                ) : null}
              </Stack>

              {/* Rating + feedback — always visible for staff, not on draft */}
              {isStaff && detailTask.status !== 'draft' ? (
                <Card variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="caption" fontWeight={600}>Rating:</Typography>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Chip
                          key={n} label={n} size="small"
                          variant={approveRating === n ? 'filled' : 'outlined'}
                          color={approveRating === n ? (n >= 3 ? 'success' : 'error') : 'default'}
                          onClick={() => setApproveRating(approveRating === n ? 0 : n)}
                          sx={{ minWidth: 28, height: 24, cursor: 'pointer' }}
                        />
                      ))}
                    </Stack>
                    <TextField
                      size="small" fullWidth multiline minRows={1} maxRows={3}
                      placeholder="Feedback note (optional)..."
                      value={ratingNote}
                      onChange={(e) => setRatingNote(e.target.value)}
                      InputProps={{ sx: { fontSize: '0.8rem' } }}
                    />
                    <Button size="small" variant="outlined" sx={{ alignSelf: 'flex-start' }} disabled={ratingSaving || (!approveRating && !ratingNote.trim())} onClick={async () => {
                      setRatingSaving(true);
                      try {
                        const res = await rateCreativeTask(detailTask.id, { rating: approveRating || null, rating_note: ratingNote });
                        setDetailTask(res.data);
                      } catch (err) { setError(getApiErrorMessage(err, 'Failed to save rating')); }
                      setRatingSaving(false);
                    }}>
                      {ratingSaving ? 'Saving...' : 'Save Rating'}
                    </Button>
                  </Stack>
                </Card>
              ) : null}

              </>) : null}

              {/* Submit + revision confirmation cards live OUTSIDE the
                  show-everything wrapper so the focused-mode Next Action CTA
                  (which just flips submitOpen/revisionOpen) can show its
                  confirmation. */}
              {submitOpen ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2">Submit for Review</Typography>
                    {!detailTask.frame_io_url ? (
                      <Alert severity="warning" variant="outlined">Frame.io link is required before submitting. Paste it above.</Alert>
                    ) : null}
                    <TextField multiline minRows={2} fullWidth placeholder="Add a note about this submission (optional)..." value={submitComment} onChange={(e) => setSubmitComment(e.target.value)} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => { setSubmitOpen(false); setSubmitComment(''); }}>Cancel</Button>
                      <Button size="small" variant="contained" disabled={!detailTask.frame_io_url || actionLoading === 'submit'} onClick={handleSubmitTask}>
                        {actionLoading === 'submit' ? 'Submitting...' : 'Submit'}
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              ) : null}

              {revisionOpen ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2">Request Revision</Typography>
                    <TextField multiline minRows={2} fullWidth placeholder="What needs to change?" value={revisionText} onChange={(e) => setRevisionText(e.target.value)} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => { setRevisionOpen(false); setRevisionText(''); }}>Cancel</Button>
                      <Button size="small" variant="contained" color="warning" disabled={!revisionText.trim() || actionLoading === 'revision'} onClick={handleRequestRevision}>Send</Button>
                    </Stack>
                  </Stack>
                </Card>
              ) : null}

              {/* Comments — hidden in focused mode */}
              {show('comments') ? (<><Divider />
              <Typography variant="subtitle2">Comments ({(detailTask.comments || []).length})</Typography>
              {(detailTask.comments || []).map((c) => (
                <Card key={c.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" fontWeight={700}>{c.author_name || c.author_email}</Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(c.created_at).toLocaleString()}</Typography>
                    </Stack>
                    <Typography variant="body2">{c.body}</Typography>
                  </Stack>
                </Card>
              ))}
              <Stack direction="row" spacing={1}>
                <TextField fullWidth size="small" placeholder="Add a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }} />
                <Button variant="contained" size="small" disabled={!commentText.trim() || commenting} onClick={handleAddComment}>Send</Button>
              </Stack></>) : null}
            </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions><Button onClick={() => { setDetailOpen(false); setDetailTask(null); }}>Close</Button></DialogActions>
      </Dialog>

      {/* ========== BULK APPLY DIALOG ========== */}
      <Dialog open={bulkApplyOpen} onClose={() => setBulkApplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply to {selectedTaskIds.size} Selected Tasks</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Only set the fields you want to change. Empty fields will be left as-is.
            </Typography>
            <TextField select fullWidth label="Vertical" value={bulkForm.vertical} onChange={(e) => handleBulkVerticalChange(e.target.value)}>
              <MenuItem value="">— Don't change —</MenuItem>
              {verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField select fullWidth label="Avatar" value={bulkForm.avatar} onChange={(e) => setBulkForm((p) => ({ ...p, avatar: e.target.value }))} disabled={!bulkForm.vertical}>
                  <MenuItem value="">— Don't change —</MenuItem>
                  {bulkAvatars.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth label="Pain Point" value={bulkForm.pain_point} onChange={(e) => setBulkForm((p) => ({ ...p, pain_point: e.target.value }))} disabled={!bulkForm.vertical}>
                  <MenuItem value="">— Don't change —</MenuItem>
                  {bulkPainPoints.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <TextField select fullWidth label="Style" value={bulkForm.style} onChange={(e) => setBulkForm((p) => ({ ...p, style: e.target.value }))}>
              <MenuItem value="">— Don't change —</MenuItem>
              {styles.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Audience" value={bulkForm.audience} onChange={(e) => setBulkForm((p) => ({ ...p, audience: e.target.value }))}>
              <MenuItem value="">— Don't change —</MenuItem>
              {AUDIENCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Designer" value={bulkForm.designer} onChange={(e) => setBulkForm((p) => ({ ...p, designer: e.target.value }))}>
              <MenuItem value="">— Don't change —</MenuItem>
              {designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkApplyOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkApply} disabled={bulkApplying || !Object.values(bulkForm).some(Boolean)}>
            {bulkApplying ? 'Applying...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== MANAGE LOOKUPS DIALOG ========== */}
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Lookups</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {manageError ? <Alert severity="error" sx={{ mb: 1 }} onClose={() => setManageError('')}>{manageError}</Alert> : null}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Tabs value={manageTab} onChange={(_e, v) => setManageTab(v)} variant="scrollable" scrollButtons="auto" sx={{ flex: 1 }}>
                <Tab label={`Avatars (${manageAvatars.length})`} />
                <Tab label={`Pain Points (${managePainPoints.length})`} />
                <Tab label={`Angles (${manageAngles.length})`} />
                <Tab label={`Hook Types (${manageHookTypes.length})`} />
                <Tab label={`Ad Types (${manageAdTypes.length})`} />
                <Tab label={`Styles (${manageStyles.length})`} />
                <Tab label={`Designers (${manageDesigners.length})`} />
                <Tab label={`Groups (${manageGroups.length})`} />
                <Tab label="Script Prompt" />
                <Tab label="Ad Copy Prompt" />
                <Tab label="Judge Prompt" />
              </Tabs>
              <FormControlLabel control={<Switch size="small" checked={showArchived} onChange={handleToggleShowArchived} />} label={<Typography variant="caption">Archived</Typography>} sx={{ ml: 1, mr: 0 }} />
            </Stack>

            {manageTab <= 1 ? (
              <>
                <TextField select fullWidth label="Vertical" value={manageVertical} onChange={(e) => handleManageVerticalChange(e.target.value)}>
                  <MenuItem value="">Select a vertical</MenuItem>
                  {verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                </TextField>
                {!manageVertical ? <Typography variant="body2" color="text.secondary">Select a vertical first.</Typography>
                  : manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack>
                  : manageTab === 0 ? renderLookupTable(manageAvatars, 'avatar', 'name')
                  : renderLookupTable(managePainPoints, 'pain_point', 'label')}
              </>
            ) : null}
            {manageTab === 2 ? (manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : renderLookupTable(manageAngles, 'angle')) : null}
            {manageTab === 3 ? (manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : renderLookupTable(manageHookTypes, 'hook_type')) : null}
            {manageTab === 4 ? (manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : renderLookupTable(manageAdTypes, 'ad_type')) : null}
            {manageTab === 5 ? (manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : renderLookupTable(manageStyles, 'style')) : null}
            {manageTab === 6 ? (
              manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : (
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="flex-end"><Button size="small" startIcon={<AddIcon />} onClick={() => openDesignerDialog()}>Add Designer</Button></Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Slug</TableCell><TableCell>Email</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                      <TableBody>
                        {manageDesigners.length === 0 ? <TableRow><TableCell colSpan={4} align="center">None.</TableCell></TableRow> : manageDesigners.map((d) => (
                          <TableRow key={d.id} hover sx={!d.active ? { opacity: 0.5 } : {}}>
                            <TableCell>{d.display_name || d.slug}{!d.active ? <Chip label="Archived" size="small" sx={{ ml: 1, height: 18 }} /> : null}</TableCell>
                            <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{d.slug}</Typography></TableCell>
                            <TableCell><Typography variant="caption">{d.user_email}</Typography></TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => openDesignerDialog(d)}><EditIcon fontSize="small" /></IconButton>
                              <Button size="small" sx={{ minWidth: 0, ml: 0.5, fontSize: '0.7rem' }} color={d.active ? 'warning' : 'success'} disabled={manageSaving} onClick={() => handleToggleDesignerActive(d.id, d.active)}>{d.active ? 'Archive' : 'Restore'}</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )
            ) : null}

            {/* AI Prompt tab */}
            {/* Groups tab */}
            {manageTab === 7 ? (
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button size="small" startIcon={<AddIcon />} onClick={() => {
                    setEditingItem({ type: 'group', name: '', slug: '', members: [] });
                    setEditDialogOpen(true);
                  }}>Add Group</Button>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Slug</TableCell><TableCell>Members</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                    <TableBody>
                      {manageGroups.length === 0 ? <TableRow><TableCell colSpan={4} align="center">No groups yet.</TableCell></TableRow> : manageGroups.map((g) => (
                        <TableRow key={g.id}>
                          <TableCell>{g.name}</TableCell>
                          <TableCell><Typography variant="caption" fontFamily="monospace">{g.slug}</Typography></TableCell>
                          <TableCell>{(g.member_names || []).join(', ') || '—'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => {
                              setEditingItem({ type: 'group', id: g.id, name: g.name, slug: g.slug, members: g.members || [] });
                              setEditDialogOpen(true);
                            }}><EditIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            ) : null}

            {manageTab === 8 ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  This is the exact system prompt sent to Claude when generating scripts. Edit it to change tone, add rules, adjust output format, etc. Task context (client, vertical, avatar, offers) is automatically appended.
                </Typography>
                <TextField
                  multiline fullWidth minRows={12} maxRows={24}
                  value={scriptPromptDraft}
                  onChange={(e) => setScriptPromptDraft(e.target.value)}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={() => setScriptPromptDraft(scriptPrompt?.system_prompt || '')} disabled={scriptPromptDraft === (scriptPrompt?.system_prompt || '')}>
                    Reset
                  </Button>
                  <Button size="small" variant="contained" onClick={handleSavePrompt} disabled={manageSaving || !scriptPromptDraft.trim() || scriptPromptDraft === (scriptPrompt?.system_prompt || '')}>
                    {manageSaving ? 'Saving...' : 'Save Prompt'}
                  </Button>
                </Stack>
              </Stack>
            ) : null}

            {manageTab === 9 ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  This is the system prompt sent to Claude when generating Facebook ad copy (headline, primary text, description). Task context, script, and client offers are automatically appended.
                </Typography>
                <TextField
                  multiline fullWidth minRows={12} maxRows={24}
                  value={adCopyPromptDraft}
                  onChange={(e) => setAdCopyPromptDraft(e.target.value)}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={() => setAdCopyPromptDraft(adCopyPrompt?.system_prompt || '')} disabled={adCopyPromptDraft === (adCopyPrompt?.system_prompt || '')}>
                    Reset
                  </Button>
                  <Button size="small" variant="contained" onClick={handleSaveAdCopyPrompt} disabled={manageSaving || !adCopyPromptDraft.trim() || adCopyPromptDraft === (adCopyPrompt?.system_prompt || '')}>
                    {manageSaving ? 'Saving...' : 'Save Prompt'}
                  </Button>
                </Stack>
              </Stack>
            ) : null}

            {manageTab === 10 ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  This is the system prompt sent to Claude for the "Rank with AI" judge pass that ranks variant scripts head-to-head. Task context (client, vertical, avatar, pain point, ad format, audience, client-specific rules) and the candidate scripts are automatically appended. Recent thumbs-up / thumbs-down scripts from this client are also injected as calibration. If you want JSON output, ask for it here — the backend parses both JSON and line formats.
                </Typography>
                {!judgePrompt ? (
                  <Alert severity="info">No default SelectionPromptTemplate row found. Run the migrations first, then reopen this dialog.</Alert>
                ) : null}
                <TextField
                  multiline fullWidth minRows={12} maxRows={24}
                  value={judgePromptDraft}
                  onChange={(e) => setJudgePromptDraft(e.target.value)}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                  disabled={!judgePrompt}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={() => setJudgePromptDraft(judgePrompt?.system_prompt || '')} disabled={!judgePrompt || judgePromptDraft === (judgePrompt?.system_prompt || '')}>
                    Reset
                  </Button>
                  <Button size="small" variant="contained" onClick={handleSaveJudgePrompt} disabled={!judgePrompt || manageSaving || !judgePromptDraft.trim() || judgePromptDraft === (judgePrompt?.system_prompt || '')}>
                    {manageSaving ? 'Saving...' : 'Save Prompt'}
                  </Button>
                </Stack>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setManageOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* ========== ADD/EDIT LOOKUP ITEM DIALOG ========== */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem?.id ? 'Edit' : 'Add'} {{ avatar: 'Avatar', pain_point: 'Pain Point', angle: 'Angle', hook_type: 'Hook Type', ad_type: 'Ad Type', style: 'Style', group: 'Designer Group' }[editingItem?.type] || 'Item'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {manageError ? <Alert severity="error">{manageError}</Alert> : null}
            <TextField label={editingItem?.type === 'avatar' ? 'Name' : editingItem?.type === 'group' ? 'Group Name' : 'Label'} fullWidth required value={editingItem?.name || ''} onChange={(e) => {
              const name = e.target.value;
              const max = ['angle', 'style', 'ad_type'].includes(editingItem?.type) ? 10 : 20;
              setEditingItem((p) => ({ ...p, name, ...(!p?.id ? { slug: autoSlug(name).slice(0, max) } : {}) }));
            }} />
            <TextField label="Slug" fullWidth required value={editingItem?.slug || ''} onChange={(e) => {
              const max = ['angle', 'style', 'ad_type'].includes(editingItem?.type) ? 10 : 20;
              setEditingItem((p) => ({ ...p, slug: e.target.value.slice(0, max) }));
            }} helperText={(() => { const max = ['angle', 'style', 'ad_type'].includes(editingItem?.type) ? 10 : 20; return `${(editingItem?.slug || '').length}/${max}`; })()} InputProps={{ sx: { fontFamily: 'monospace' } }} />
            {editingItem?.type === 'group' ? (
              <Autocomplete
                multiple
                options={manageDesigners}
                getOptionLabel={(o) => o.display_name || o.slug}
                value={manageDesigners.filter((d) => (editingItem.members || []).includes(d.id))}
                onChange={(_e, v) => setEditingItem((p) => ({ ...p, members: v.map((d) => d.id) }))}
                renderInput={(params) => <TextField {...params} label="Members" placeholder="Add designers..." />}
                renderTags={(value, getTagProps) => value.map((option, index) => <Chip {...getTagProps({ index })} key={option.id} label={option.display_name || option.slug} size="small" />)}
              />
            ) : null}
            {['avatar', 'pain_point'].includes(editingItem?.type) ? (
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={3}
                value={editingItem?.description || ''}
                onChange={(e) => setEditingItem((p) => ({ ...p, description: e.target.value }))}
                placeholder={editingItem?.type === 'avatar'
                  ? 'Who this persona is and what they care about. Used by the AI selection engine to judge combos. e.g. "Homeowners with dogs/young kids; worry about floor damage from scratches and spills. Value durability over aesthetics."'
                  : 'What this pain point means and which avatars it pairs with. e.g. "Water entering the home right now — urgent, high-intent buyer. Pairs with safety/repair avatars. Does NOT pair with luxury."'}
                helperText="Optional but recommended — the selection engine uses this to reason about valid combos."
              />
            ) : null}
            {editingItem?.type === 'hook_type' ? (
              <TextField
                label="Priority (0 = most preferred, 100 = least)"
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 100 }}
                value={editingItem?.priority ?? 50}
                onChange={(e) => setEditingItem((p) => ({ ...p, priority: e.target.value === '' ? '' : Number(e.target.value) }))}
                helperText="Soft bias for the AI auto-picker. Default 50. Lower the number to make this hook get picked more often when nothing in the task context strongly favors a particular hook."
              />
            ) : null}
            {editingItem?.type === 'style' ? (
              <>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  minRows={3}
                  value={editingItem?.description || ''}
                  onChange={(e) => setEditingItem((p) => ({ ...p, description: e.target.value }))}
                  placeholder='What this style looks/sounds like. e.g. "UGC: shot on phone, single talking-head testimonial, natural lighting, casual unscripted tone — feels like a friend recommending."'
                  helperText="Surfaced on the task page so designers know what to deliver. Also fed into the script generator."
                />
                <TextField
                  label="Example URL"
                  fullWidth
                  type="url"
                  value={editingItem?.reference_url || ''}
                  onChange={(e) => setEditingItem((p) => ({ ...p, reference_url: e.target.value }))}
                  placeholder="https://www.dropbox.com/... or https://drive.google.com/..."
                  helperText="Link to an example video, reel, or dropbox folder showing this style."
                />
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveItem} disabled={manageSaving || !editingItem?.name?.trim() || !editingItem?.slug?.trim()}>{manageSaving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ========== ADD/EDIT DESIGNER DIALOG ========== */}
      <Dialog open={designerDialogOpen} onClose={() => setDesignerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDesigner?.id ? 'Edit' : 'Add'} Designer</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {manageError ? <Alert severity="error">{manageError}</Alert> : null}
            <Autocomplete
              options={staffUsers}
              getOptionLabel={(o) => { const n = `${o.first_name || ''} ${o.last_name || ''}`.trim(); return n ? `${n} (${o.email})` : o.email || ''; }}
              value={staffUsers.find((u) => u.id === editingDesigner?.user) || null}
              onChange={(_e, v) => {
                const name = v ? `${v.first_name || ''} ${v.last_name || ''}`.trim() : '';
                setEditingDesigner((p) => ({ ...p, user: v?.id || '', display_name: p?.display_name || name, ...(!p?.id && !p?.slug ? { slug: autoSlug(v?.first_name || v?.email?.split('@')[0] || '') } : {}) }));
              }}
              disabled={Boolean(editingDesigner?.id)}
              renderInput={(params) => <TextField {...params} label="User" required helperText={editingDesigner?.id ? 'Cannot change after creation' : ''} />}
            />
            <TextField label="Display Name" fullWidth value={editingDesigner?.display_name || ''} onChange={(e) => setEditingDesigner((p) => ({ ...p, display_name: e.target.value }))} />
            <TextField label="Slug" fullWidth required value={editingDesigner?.slug || ''} onChange={(e) => setEditingDesigner((p) => ({ ...p, slug: e.target.value.slice(0, 20) }))} helperText={`cr= component. ${(editingDesigner?.slug || '').length}/20`} InputProps={{ sx: { fontFamily: 'monospace' } }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDesignerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDesigner} disabled={manageSaving || !editingDesigner?.user || !editingDesigner?.slug?.trim()}>{manageSaving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ========== LAUNCH TO FACEBOOK MODAL ========== */}
      <Dialog open={fbLaunchOpen} onClose={() => { setFbLaunchOpen(false); setFbLaunchResult(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Launch to Facebook{fbLaunchTask?.task_code ? ` — ${fbLaunchTask.task_code}` : ''}</DialogTitle>

        {/* Result banner — outside DialogContent so it's always visible */}
        {fbLaunchResult?.success ? (
          <Alert severity="success" sx={{ mx: 3, mb: 0, '& .MuiAlert-message': { width: '100%' } }}>
            <Stack spacing={1}>
              <Typography variant="body2" fontWeight={600}>
                {fbLaunchResult.ad_count > 1
                  ? `${fbLaunchResult.ad_count} ads created successfully! (status: PAUSED)`
                  : 'Ad created successfully! (status: PAUSED)'}
              </Typography>
              <Typography variant="body2">Ad Name: <strong>{fbLaunchResult.ad_name}</strong></Typography>
              {fbLaunchResult.facebook_creative_id ? <Typography variant="body2">Creative ID: {fbLaunchResult.facebook_creative_id}</Typography> : null}
              {(fbLaunchResult.ads || []).map((ad) => (
                <Typography key={ad.ad_id} variant="body2">
                  {ad.ad_set_name}: <strong>{ad.ad_id}</strong>
                </Typography>
              ))}
              <Button
                size="small" variant="outlined" color="success"
                component="a"
                href={`https://www.facebook.com/adsmanager/manage/ads?act=${fbLaunchForm.ad_account_id}&selected_adset_ids=${(fbLaunchResult.ads || []).map((a) => a.ad_set_id).join(',')}`}
                target="_blank" rel="noopener noreferrer"
                startIcon={<OpenInNewIcon />}
                sx={{ alignSelf: 'flex-start', mt: 0.5 }}
              >
                View in Ads Manager
              </Button>
            </Stack>
          </Alert>
        ) : fbLaunchResult?.error ? (
          <Alert severity="error" sx={{ mx: 3, mb: 0 }}>
            <Typography variant="body2" fontWeight={600}>Launch failed</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{fbLaunchResult.error}</Typography>
          </Alert>
        ) : null}

        <DialogContent dividers>
          <Stack spacing={2.5}>
            {fbLaunchResult?.dry_run ? (
              <Alert severity="info">Preview — no ad was created. Review the details below and click "Launch" to push to Facebook.</Alert>
            ) : null}

            {/* Creative asset preview — show at top */}
            {fbLaunchTask && (fbLaunchTask.files || []).length > 0 ? (
              <>
                <Typography variant="subtitle2">Creative Assets</Typography>
                <Stack spacing={1}>
                  {(fbLaunchTask.files || []).map((file) => {
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.original_name || file.file || '');
                    const isVideo = /\.(mp4|mov|webm|avi)$/i.test(file.original_name || file.file || '');
                    return (
                      <Card key={file.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          {isImage ? (
                            <Box component="img" src={file.file} alt={file.original_name} sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1 }} />
                          ) : isVideo ? (
                            <Box component="video" src={file.file} controls sx={{ width: 200, maxHeight: 120, borderRadius: 1 }} />
                          ) : null}
                          <Stack spacing={0.25} sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{file.original_name || 'File'}</Typography>
                            <Chip label={file.file_type} size="small" variant="outlined" sx={{ alignSelf: 'flex-start', height: 18 }} />
                          </Stack>
                          <Button size="small" component="a" href={file.file} target="_blank" rel="noopener noreferrer">View</Button>
                        </Stack>
                      </Card>
                    );
                  })}
                </Stack>
                <Typography variant="caption" color="text.secondary">The first deliverable file (video preferred) will be uploaded to Facebook with the ad.</Typography>
                <Divider />
              </>
            ) : null}

            {/* Account / Campaign / Ad Set selectors */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">Facebook Placement</Typography>
              {fbStructureLoading ? <CircularProgress size={16} /> : null}
              <Button size="small" variant="outlined" color="inherit" sx={{ ml: 'auto', fontSize: '0.7rem', py: 0.25 }} disabled={fbStructureLoading} onClick={async () => {
                setFbStructureLoading(true);
                setFbLaunchForm((prev) => ({ ...prev, ad_account_id: '', campaign_id: '', ad_set_ids: [] }));
                try {
                  const res = await fetchFacebookAdStructure(fbLaunchTask.client, fbShowInactive);
                  setFbStructure(res?.data || []);
                } catch (_e) { setFbStructure([]); }
                setFbStructureLoading(false);
              }}>
                Refresh from Facebook
              </Button>
              <Button size="small" variant={fbShowInactive ? 'contained' : 'outlined'} color="inherit" sx={{ fontSize: '0.7rem', py: 0.25 }} disabled={fbStructureLoading} onClick={async () => {
                const next = !fbShowInactive;
                setFbShowInactive(next);
                setFbStructureLoading(true);
                setFbLaunchForm((prev) => ({ ...prev, ad_account_id: '', campaign_id: '', ad_set_ids: [] }));
                try {
                  const res = await fetchFacebookAdStructure(fbLaunchTask.client, next);
                  setFbStructure(res?.data || []);
                } catch (_e) { setFbStructure([]); }
                setFbStructureLoading(false);
              }}>
                {fbShowInactive ? 'Showing Inactive' : 'Include Inactive'}
              </Button>
            </Stack>
            <Stack spacing={2}>
              <TextField select label="Ad Account" fullWidth value={fbLaunchForm.ad_account_id} disabled={fbStructureLoading} onChange={(e) => {
                setFbLaunchForm((prev) => ({ ...prev, ad_account_id: e.target.value, campaign_id: '', ad_set_ids: [] }));
              }}>
                {fbStructure.map((acct) => (
                  <MenuItem key={acct.account_id} value={acct.account_id}>{acct.account_name} ({acct.account_id})</MenuItem>
                ))}
              </TextField>
              {(() => {
                const acct = fbStructure.find((a) => a.account_id === fbLaunchForm.ad_account_id);
                const campaigns = acct?.campaigns || [];
                return (
                  <>
                    <TextField select label="Campaign" fullWidth value={fbLaunchForm.campaign_id} disabled={fbStructureLoading || !campaigns.length} onChange={(e) => {
                      setFbLaunchForm((prev) => ({ ...prev, campaign_id: e.target.value, ad_set_ids: [] }));
                    }}>
                      {campaigns.map((c) => (
                        <MenuItem key={c.campaign_id} value={c.campaign_id}>{c.campaign_name} ({c.status})</MenuItem>
                      ))}
                    </TextField>
                    {/* Ad Sets — multi-select with checkboxes */}
                    {(() => {
                      const adSets = campaigns.find((c) => c.campaign_id === fbLaunchForm.campaign_id)?.ad_sets || [];
                      if (!fbLaunchForm.campaign_id) return <TextField label="Ad Sets" fullWidth disabled placeholder="Select a campaign first" />;
                      if (!adSets.length) return <Typography variant="caption" color="text.secondary">No ad sets found in this campaign.</Typography>;
                      return (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Select ad sets to launch into ({fbLaunchForm.ad_set_ids.length} selected):</Typography>
                          <Stack spacing={0.5} sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                            {adSets.map((as) => (
                              <FormControlLabel
                                key={as.ad_set_id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={fbLaunchForm.ad_set_ids.includes(as.ad_set_id)}
                                    onChange={(e) => {
                                      setFbLaunchForm((prev) => ({
                                        ...prev,
                                        ad_set_ids: e.target.checked
                                          ? [...prev.ad_set_ids, as.ad_set_id]
                                          : prev.ad_set_ids.filter((id) => id !== as.ad_set_id),
                                      }));
                                    }}
                                  />
                                }
                                label={
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2">{as.ad_set_name}</Typography>
                                    <Chip label={as.effective_status || (as.active ? 'ACTIVE' : 'INACTIVE')} size="small" sx={{ height: 18, fontSize: '0.65rem', ...(as.active ? { bgcolor: '#c8e6c9', color: '#2e7d32' } : { bgcolor: 'grey.200', color: 'grey.600' }) }} />
                                  </Stack>
                                }
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      );
                    })()}
                  </>
                );
              })()}
            </Stack>

            <Divider />

            {/* Ad copy fields */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">Ad Copy</Typography>
              <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} disabled={fbCopyGenerating} onClick={async () => {
                setFbCopyGenerating(true);
                setFbHeadlineOptions([]);
                try {
                  const res = await generateAdCopy(fbLaunchTask.id);
                  const d = res?.data || {};
                  const headlines = d.headlines || (d.headline ? [d.headline] : []);
                  setFbHeadlineOptions(headlines);
                  setFbLaunchForm((prev) => ({
                    ...prev,
                    headline: headlines[0] || prev.headline,
                    primary_text: d.primary_text || prev.primary_text,
                    description: d.description || prev.description,
                  }));
                } catch (err) { setError(getApiErrorMessage(err, 'Ad copy generation failed')); }
                setFbCopyGenerating(false);
              }}>
                {fbCopyGenerating ? 'Generating...' : 'Generate with AI'}
              </Button>
            </Stack>

            {/* Headline picker */}
            {fbHeadlineOptions.length > 1 ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Pick a headline:</Typography>
                <Stack spacing={0.5}>
                  {fbHeadlineOptions.map((h, i) => (
                    <Chip
                      key={i}
                      label={h}
                      size="small"
                      variant={fbLaunchForm.headline === h ? 'filled' : 'outlined'}
                      color={fbLaunchForm.headline === h ? 'primary' : 'default'}
                      onClick={() => setFbLaunchForm((prev) => ({ ...prev, headline: h }))}
                      sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}
            <TextField label="Headline" fullWidth value={fbLaunchForm.headline} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, headline: e.target.value }))} helperText="Selected headline — you can also edit manually" />
            <TextField label="Primary Text" fullWidth multiline minRows={3} value={fbLaunchForm.primary_text} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, primary_text: e.target.value }))} />
            <TextField label="Description" fullWidth value={fbLaunchForm.description} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, description: e.target.value }))} helperText="Short text shown below the headline (~30 chars)" />
            <Stack direction="row" spacing={2}>
              <TextField select label="CTA" sx={{ minWidth: 200 }} value={fbLaunchForm.cta_type} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, cta_type: e.target.value }))}>
                {['LEARN_MORE', 'SIGN_UP', 'SHOP_NOW', 'BOOK_TRAVEL', 'CONTACT_US', 'GET_OFFER', 'GET_QUOTE', 'SUBSCRIBE', 'CALL_NOW', 'APPLY_NOW', 'DOWNLOAD', 'ORDER_NOW'].map((cta) => (
                  <MenuItem key={cta} value={cta}>{cta.replace(/_/g, ' ')}</MenuItem>
                ))}
              </TextField>
              <TextField label="Destination URL" fullWidth value={fbLaunchForm.destination_url} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, destination_url: e.target.value }))} placeholder="https://..." helperText={fbLaunchForm.destination_url ? 'Auto-filled from client landing page' : 'No landing page found — enter manually (will be saved for future use)'} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Display Link" sx={{ minWidth: 250 }} value={fbLaunchForm.display_link} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, display_link: e.target.value }))} placeholder="hansons.com" helperText="Shown below the ad instead of the full URL" />
            </Stack>
            <TextField label="Facebook Page ID" fullWidth value={fbLaunchForm.page_id} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, page_id: e.target.value }))} helperText={fbLaunchForm.page_name ? `Auto-filled: ${fbLaunchForm.page_name}` : 'Leave blank to use Lead Gurus default page. New page IDs will be saved for future use.'} />

            {fbLaunchResult?.dry_run ? (
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Preview:</Typography>
                <Typography variant="body2"><strong>Ad Name:</strong> {fbLaunchResult.ad_name}</Typography>
                <Typography variant="body2"><strong>UTM Tags:</strong> {fbLaunchResult.url_tags}</Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFbLaunchOpen(false); setFbLaunchResult(null); }}>Close</Button>
          <Button variant="outlined" disabled={fbLaunchLoading || !fbLaunchForm.ad_account_id || !fbLaunchForm.ad_set_ids.length || !fbLaunchForm.headline || !fbLaunchForm.destination_url || fbLaunchResult?.success} onClick={async () => {
            setFbLaunchLoading(true);
            try {
              const res = await launchTaskToFacebook(fbLaunchTask.id, { ...fbLaunchForm, dry_run: true });
              setFbLaunchResult(res?.data);
            } catch (err) { setError(getApiErrorMessage(err, 'Preview failed')); }
            setFbLaunchLoading(false);
          }}>Preview</Button>
          <Button variant="contained" disabled={fbLaunchLoading || !fbLaunchForm.ad_account_id || !fbLaunchForm.ad_set_ids.length || !fbLaunchForm.headline || !fbLaunchForm.destination_url || fbLaunchResult?.success} onClick={async () => {
            setFbLaunchLoading(true);
            try {
              const res = await launchTaskToFacebook(fbLaunchTask.id, { ...fbLaunchForm, dry_run: false });
              setFbLaunchResult(res?.data);
              // Refresh task detail
              const updated = await fetchCreativeTask(fbLaunchTask.id);
              setDetailTask(updated.data);
            } catch (err) { setFbLaunchResult({ error: getApiErrorMessage(err, 'Facebook launch failed') }); }
            setFbLaunchLoading(false);
          }}>
            {fbLaunchLoading ? 'Launching...' : `Launch to ${fbLaunchForm.ad_set_ids.length || 0} Ad Set${fbLaunchForm.ad_set_ids.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== API INFO MODAL ========== */}
      <Dialog open={apiModalOpen} onClose={() => setApiModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>REST API - Designer Integration</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Alert severity="info">
              Use this API to programmatically fetch flight and task data. All endpoints require authentication via session cookie or API token.
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Base URL</Typography>
              <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                {window.location.origin}/api/creative-studio/designer-api/
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Endpoints</Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/</Typography>
                  <Typography variant="caption" color="text.secondary">List all flights and tasks visible to the authenticated user. Staff sees all; designers see only assigned.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/?client_id=123</Typography>
                  <Typography variant="caption" color="text.secondary">Filter flights by client ID.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/?status=in_progress</Typography>
                  <Typography variant="caption" color="text.secondary">Filter by flight status: draft, in_progress, complete, cancelled.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/me/</Typography>
                  <Typography variant="caption" color="text.secondary">Get current user profile and designer info.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/flights/{'<id or code>'}/</Typography>
                  <Typography variant="caption" color="text.secondary">Get a single flight by numeric ID or flight code (e.g. FLT-0008), including all its tasks.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/tasks/{'<id or code>'}/</Typography>
                  <Typography variant="caption" color="text.secondary">Get a single task by numeric ID or task code (e.g. FLT-0008-T0042), including all details.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>POST /api/creative-studio/designer-api/tasks/{'<id or code>'}/submit-link/</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Submit a Frame.io link to a task. Accepts numeric ID or task code. Body: {'{ "frame_io_url": "https://app.frame.io/..." }'}.
                    Auto-advances status: draft/assigned → in_progress, in_progress/needs_revision → submitted.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/flights/{'<id>'}/export/</Typography>
                  <Typography variant="caption" color="text.secondary">Download all tasks in a flight as CSV.</Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Example Response</Typography>
              <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'auto', maxHeight: 300 }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify({
                  count: 1,
                  user: "designer@example.com",
                  flights: [{
                    id: 1,
                    flight_code: "FLT-0001",
                    name: "March Flight",
                    status: "in_progress",
                    client: "Example Co",
                    task_count: 5,
                    tasks: [{
                      id: 42,
                      task_code: "FLT-0001-T0042",
                      structured_name: "av=bmom&pp=slip&an=cur&hk=save30&at=vid&st=ugc&au=55+&cr=jake&v=1",
                      status: "assigned",
                      client: "Example Co",
                      vertical: "Bath",
                      audience: "55+",
                      avatar: "Busy Mom",
                      pain_point: "Slippery Tub",
                      angle: "Curiosity",
                      ad_type: "Video",
                      style: "UGC Ad",
                      hook_text: "What if your bathroom is the most dangerous room?",
                      script_text: "Full script text...",
                      designer: "Jake",
                      frame_io_url: null,
                      client_context: {
                        client_name: "Example Co",
                        website_url: "https://example.com",
                        logo_url: "https://dropbox.com/...",
                        notes: "Premium brand, focus on safety...",
                        ai_script_instructions: "Bath: Always mention lifetime warranty",
                        offers: [{ financing: "12 Months No/No", ad_offer: "50% Off Installation", vertical: "Bath" }],
                        client_asset_folder_url: "https://dropbox.com/...",
                        general_asset_folder_url: "https://dropbox.com/...",
                        recent_ad_examples_url: "https://dropbox.com/...",
                      },
                    }]
                  }]
                }, null, 2)}</pre>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Push Tasks (external import)</Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                External systems (e.g. a script-generation app) can push tasks in. References are sent as
                plain <strong>names</strong> — client, vertical, designer — and are resolved on our side.
                Fully-recognized pushes create tasks immediately; anything unrecognized lands in
                “Review Imports” on this page for a one-time mapping (remembered for future pushes).
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>POST /api/creative-studio/task-imports/</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Push a batch of tasks. Send a unique <code>external_id</code> per push — retries with the same id
                    return the original batch instead of duplicating tasks. Pushes without a <code>flight</code> are grouped
                    into a shared weekly import flight automatically, so multiple pushes through the week land together.
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'auto', maxHeight: 300 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify({
                    source: 'CreativeFlights',
                    external_id: 'run-2026-07-02-001',
                    flight: 'FLT-0044 (optional — omit to auto-group by week)',
                    designer: 'jane-d (optional batch default)',
                    assign: true,
                    tasks: [{
                      client: 'Hansons',
                      vertical: 'Bath',
                      audience: 'all | 55+ | men | women',
                      designer: 'jane-d',
                      script_text: 'Full ad script... (makes it a custom-script task)',
                      hook_text: 'optional',
                      notes: 'optional',
                      custom_ad_name: 'optional',
                    }],
                  }, null, 2)}</pre>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/task-imports/?status=pending</Typography>
                  <Typography variant="caption" color="text.secondary">List pushes awaiting review (what the “Review Imports” button shows).</Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Authentication</Typography>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Use an API token for programmatic access. Generate one below — it never expires and works with any HTTP client.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  disabled={apiTokenLoading}
                  onClick={async () => {
                    setApiTokenLoading(true);
                    try {
                      const res = await getDesignerAPIToken();
                      setApiToken(res.data);
                    } catch (_e) { /* silent */ }
                    setApiTokenLoading(false);
                  }}
                >
                  {apiTokenLoading ? 'Loading...' : apiToken ? 'Show Token' : 'Generate API Token'}
                </Button>
                {apiToken ? (
                  <Button
                    size="small"
                    color="warning"
                    disabled={apiTokenLoading}
                    onClick={async () => {
                      setApiTokenLoading(true);
                      try {
                        const res = await regenerateDesignerAPIToken();
                        setApiToken(res.data);
                      } catch (_e) { /* silent */ }
                      setApiTokenLoading(false);
                    }}
                  >
                    Regenerate
                  </Button>
                ) : null}
              </Stack>
              {apiToken ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Your API Token (keep this secret):</Typography>
                  <Box
                    sx={{ bgcolor: 'grey.900', color: 'success.light', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all', cursor: 'pointer', mt: 0.5 }}
                    onClick={() => { navigator.clipboard.writeText(apiToken.token); }}
                    title="Click to copy"
                  >
                    {apiToken.token}
                  </Box>
                  <Typography variant="caption" color="text.secondary">Click to copy</Typography>
                </Box>
              ) : null}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Quick Test (cURL)</Typography>
              <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'auto' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{apiToken
                  ? `curl -H "Authorization: Token ${apiToken.token}" \\
  ${window.location.origin}/api/creative-studio/designer-api/`
                  : `curl -H "Authorization: Token YOUR_TOKEN" \\
  ${window.location.origin}/api/creative-studio/designer-api/`}</pre>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <CustomScriptDialog
        open={customScriptOpen}
        onClose={() => setCustomScriptOpen(false)}
        onCreated={() => { setCustomScriptOpen(false); loadFlights(); }}
      />

      <TaskImportReviewModal
        open={importModalOpen}
        onClose={() => { setImportModalOpen(false); loadPendingImports(); }}
        onCommitted={handleImportsChanged}
      />
    </Box>
  );
}

// Custom (manual) brief authoring for designers on the Flights page. Designers
// don't have Creative Engine access, so they can't reach ConceptSystemPage's
// version — this is the same idea, scoped to single-client mode (designers
// think client-by-client, not lane-by-lane) and exposed to any logged-in user.
// Backend lands the brief as status=APPROVED, ready for staff to deploy/assign.
function CustomScriptDialog({ open, onClose, onCreated }) {
  const [clientId, setClientId] = useState('');
  const [clientOffers, setClientOffers] = useState([]);
  const [clientOffersLoading, setClientOffersLoading] = useState(false);
  const [clientOfferId, setClientOfferId] = useState('');
  const [concepts, setConcepts] = useState([]);
  const [conceptSlug, setConceptSlug] = useState('');
  const [conceptDetail, setConceptDetail] = useState(null);
  const [conceptLoading, setConceptLoading] = useState(false);
  const [cellId, setCellId] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  useEffect(() => {
    if (!open) return;
    listAdConcepts()
      .then((r) => {
        const data = r?.data ?? r;
        setConcepts(Array.isArray(data) ? data : (data?.results || []));
      })
      .catch(() => setConcepts([]));
  }, [open]);

  useEffect(() => {
    if (!clientId) {
      setClientOffers([]); setClientOfferId('');
      return;
    }
    setClientOffersLoading(true);
    fetchClientOffers({ client_id: clientId, offer_type: 'offer', page_size: 100 })
      .then((r) => {
        const data = r?.data ?? r;
        setClientOffers(Array.isArray(data) ? data : (data?.results || []));
      })
      .catch(() => setClientOffers([]))
      .finally(() => setClientOffersLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (!conceptSlug) {
      setConceptDetail(null); setCellId('');
      return;
    }
    setConceptLoading(true);
    setCellId('');
    fetchAdConcept(conceptSlug)
      .then((r) => setConceptDetail(r?.data ?? r))
      .catch(() => setConceptDetail(null))
      .finally(() => setConceptLoading(false));
  }, [conceptSlug]);

  const cellOptions = conceptDetail?.cells || conceptDetail?.sub_axis_cells || [];

  const reset = () => {
    setClientId(''); setClientOffers([]); setClientOfferId('');
    setConceptSlug(''); setConceptDetail(null); setCellId('');
    setMarkdown(''); setNotes(''); setError(null);
  };

  const close = () => { reset(); onClose(); };

  const canSubmit = !!clientId
    && (!conceptSlug || !!cellId)
    && markdown.trim().length > 20;

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const payload = {
        client_id: Number(clientId),
        rendered_brief: markdown,
      };
      if (clientOfferId) payload.client_offer_id = Number(clientOfferId);
      if (cellId) payload.cell_id = Number(cellId);
      if (notes.trim()) payload.notes = notes.trim();
      await createManualBrief(payload);
      reset();
      onCreated();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not save brief'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Custom script</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Hand-write the script instead of generating it. No AI cost.
            Lands as <strong>Approved</strong> and is ready to be deployed +
            assigned by staff.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                size="small"
                options={clientOptions}
                loading={clientOptionsLoading}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => String(o.value) === String(v?.value)}
                value={findClientOptionByValue(clientId)}
                onInputChange={handleClientSearch}
                onChange={(_e, v) => setClientId(v?.value ? String(v.value) : '')}
                renderInput={(params) => (
                  <TextField {...params} label="Client" required placeholder="Search for a client…" />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select size="small" fullWidth
                label="Offer (optional)"
                value={clientOfferId}
                onChange={(e) => setClientOfferId(e.target.value)}
                disabled={!clientId || clientOffersLoading}
                helperText={clientId
                  ? (clientOffersLoading
                      ? 'Loading offers…'
                      : "Blank = auto-resolve client's current AD offer.")
                  : 'Pick a client first'}
              >
                <MenuItem value=""><em>Auto-resolve current offer</em></MenuItem>
                {clientOffers.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.display_label || o.custom_label || `Offer ${o.id}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select size="small" fullWidth label="Concept (optional)"
                value={conceptSlug}
                onChange={(e) => setConceptSlug(e.target.value)}
                helperText={conceptSlug
                  ? 'Slots into the matrix under this concept.'
                  : 'Freeform — no matrix slotting.'}
              >
                <MenuItem value=""><em>Freeform (no concept)</em></MenuItem>
                {concepts.map((c) => (
                  <MenuItem key={c.slug} value={c.slug}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              {conceptSlug ? (
                <TextField
                  select size="small" fullWidth required label="Cell (recipe)"
                  value={cellId}
                  onChange={(e) => setCellId(e.target.value)}
                  disabled={conceptLoading}
                  helperText={conceptLoading ? 'Loading cells…' : 'Which recipe is this script?'}
                >
                  <MenuItem value=""><em>— select —</em></MenuItem>
                  {cellOptions.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}{c.maturity === 'experimental' ? ' 🧪' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                  Lands in the freeform bucket — won't be matched by the AI composer.
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Your script (markdown). Type <code>{'{{'}</code> to insert a dynamic client field.
              </Typography>
              <BriefMarkdownEditor
                value={markdown}
                onChange={setMarkdown}
                placeholder={'# Your headline\n\n## Hook (0-3s)\n…'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small" fullWidth
                label="Internal notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline minRows={2}
              />
            </Grid>
          </Grid>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={14} /> : <SendIcon />}
          onClick={submit}
          disabled={submitting || !canSubmit}
        >
          {submitting ? 'Saving…' : 'Create brief'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
